
import Vue from 'vue'
import axios, { generateRequestId } from '@/lib/axiosClient'
import * as sync from '@/lib/requestRowSync'
import { copyText } from '@/lib/requestSyncCopy'
import { hasRemarksText as hasRemarksTextHelper } from '@/lib/remarksText'
import { waitForSaves } from '@/lib/saveWait'
import { keepalivePut, keepalivePost } from '@/lib/keepalive'
import * as commandNotices from '@/lib/commandNotices'
import * as savePopup from '@/lib/savePopup'
import * as approvalLock from '@/lib/approvalLock'
import { recipientPaths, applyOps } from '@/lib/requestRecipients'
import { activityText, notifyOutcome, noticeOutcome, showToast, sendLabel } from '@/lib/requestActivity'
import ArrayMixins from '@/mixins/ArrayMixins'
import SiteMixins from '@/mixins/SiteMixins'

// XOL-593: the API column holding the moment an approval decision was
// recorded (xolas-tms-api, action_steps). SERVER-OWNED (XOL-596): the approval
// command stamps it, the response carries it, and it is adopted like the other
// step-owned keys (lib/requestRowSync STEP_OWNED_KEYS). The SPA only reads it;
// it never writes it, not even optimistically. If the API renames the column,
// change THIS line only.
export const STEP_DECIDED_AT = 'decided_at'


// XOL-596: a plain-object merge for outcome objects (no Object.assign / spread
// on the Babel 6 build).
function extend (target, source) {
  if (source) {
    Object.keys(source).forEach(function (k) {
      target[k] = source[k]
    })
  }
  return target
}


// Recipient lists are re-applied onto the server's current list after a 409
// (plan R2.9); every other list (equipment) is shown and redone by the user.
// XOL-586: the six `emails.<party>.<list>` paths (Tower Co, owner, provider).
var RECIPIENT_PATHS = recipientPaths()

// The step-3 legal tick: an action `state` leaf mirrored from the step-3 approval.
var LEGAL_PATH = 'state.step3.legalApproved'

// XOL-67: the "already e-mailed" batch of the row a Send / Re-send button belongs to: a step's
// `approvalMail`, or (event 'rta_decision') the Tower Co decision's `decisionMail`. The server nulls
// it the moment the e-mailed content changes, so a set one means "Re-send".
function mailOfDecision (row, event) {
  if (!row) {
    return null
  }

  return (event === 'rta_decision' ? row.decisionMail : row.approvalMail) || null
}

// XOL-596 (completed steps): the request types that have a Complete Step control. PRG
// has none (its `completed` switches are task items inside `state`), so it never freezes.
var FROZEN_TYPES = ['RTS', 'RTU', 'RTB', 'RTD']

export default {
  mixins: [ArrayMixins, SiteMixins],

  // XOL-596: the form scope of the request drawer this component sits in
  // (RequestWorkflow provides it). Null outside a drawer: rows then have no pause
  // timer and are saved only by an explicit flush.
  inject: {
    requestSyncScope: {
      default: null
    },

    // XOL-596 (slice 3, UF2): the drawer's command host { busy(Boolean), close() }.
    // A child's running command reaches RequestWorkflow's `drawerBusy` through it, so
    // the drawer can't close mid-command (it does NOT grey the footer: only the footer's
    // own commands do, `savingStep`).
    requestCommandHost: {
      default: null
    },

    // XOL-596 (completed steps): the id of the drawer's own step-completed hint, the focus
    // target of a refusal. Per instance (two drawers can be mounted).
    requestHintId: {
      default: null
    }
  },

  data () {
    return {
      // Explicit commands running on THIS component (close permanently, lock, unlock):
      // the lock / unlock actions refuse a second one while it is above zero. Nothing
      // binds it to a control any more: the approval controls are decision fields and
      // save like every other field (XOL-596, Carlos 2026-10-14), never disabled by a
      // save.
      cmdRunning: 0,

      // The lock command running from this component's lock line: 'lock' |
      // 'unlock' | null. Names the C-7 beside the line's button.
      lockRunning: null,

      // XOL-586: the Send / Re-send {status} e-Mail of step 2 or 5 is running (the
      // confirm is answered; the save before it and the notify). The button shows it.
      approvalMailSending: false,

      // XOL-586 (Delta 1, item 4): the Re-send recipient picker of step 2 / step 5. A Re-send
      // opens it (after the flush); the first Send never does.
      //   visible  the dialog is open
      //   loading  GET .../notify/preview is running (the list stays while it refreshes)
      //   preview  the 200 body: the live recipients and `version`
      //   error    null | 'generic' | 'notFound' | 'forbidden'
      //   sending  the POST .../notify with the selection is running
      //   stale    null | { reason, unknown }: the last send was refused with 409 preview_stale
      //   event    which e-mail it re-sends: 'approval_notice' (a step 2 / 5 decision) or, for an RTA
      //            (XOL-67), 'rta_decision' (the Tower Co decision, which has no step)
      //   stepId / status / hadMail  the step (or decision) it was opened from (the loading title)
      //   opener   the element focus returns to when it closes
      resendPicker: {
        visible: false,
        loading: false,
        preview: null,
        error: null,
        sending: false,
        stale: null,
        event: 'approval_notice',
        stepId: null,
        status: null,
        hadMail: true,
        opener: null
      },

      // Bumped by every preview request and by a close, so an answer that arrives for a picker
      // that was closed or re-opened meanwhile is dropped.
      resendSeq: 0
    }
  },

  computed: {
    isBackhaul: function () {
      return this.actionData.actionType === 'RTB'
    },

    isPending: function () {
      return this.step.approvedStatus === 'Pending'
    },

    isRejected: function () {
      return this.step.approvedStatus === 'Rejected'
    },

    approvalButton: function () {
      let type = 'info'

      if (this.step.approvedStatus === 'Approved') {
        type = 'success'
      }

      if (this.step.approvedStatus === 'Conditional') {
        type = 'warning'
      }

      if (this.step.approvedStatus === 'Rejected') {
        type = 'danger'
      }

      return type
    }
  },

  methods: {

    isActive (data) {

      if (data) {
        return !(data.status === 'Closed' || data.status === 'Rejected')

      } else {
        return false
      }

    },



    requestTableRef (type) {
      return type.toLowerCase() + '-action-table'
    },



    /**
    * Get file URL given site_id, step, etc.
    */
    url (siteId, id, step = null, title = null, caption = null, uploadBy = null) {

      var u = this.getEndpoint('/file/upload/site/' + siteId + '/rts/' + id)

      if (step) {
        u += '/' + step
      }

      if (title) {
        u += '/' + title
      }

      if (title && step) {   // Provides caption for upload.
        u += '/' + title + ' for  step ' + step
      } else {
        if (step) {
          u += '/Request Step ' + step + ' doc'
        } else {
          u += '/' + (title ? title : (step ? 'Request Step ' + step + ' Doc' : null))
        }
      }

      if ((step || title) && this.getStore().loggedName) {
        u += '/' + this.getStore().loggedName
      }

      return u
    },



    // XOL-586 (F2, XOL-590): an upload into a request is announced by the API itself,
    // from the upload. There is no call to make here any more (the old upload-alert POST
    // is gone, and so is the SPA's own recipient and body list): the response of the
    // upload carries `notice`, what the server did about the e-mail, and this shows it as
    // the one toast of the upload. `notice` is null for a file that is not in a request.
    afterUpload (response) {
      this.showNoticeToast(response ? response.notice : null, { kind: 'upload_notice' })
    },

    // A `notice` (the key of an upload response or of the Tower Co's PUT /file/{id}) as
    // its toast. `extra` = { kind } when the notice has no `kind` of its own.
    showNoticeToast (notice, extra) {
      var action = this.actionData || this.rowData
      var ctx = extend({
        rtx: action && action.actionType ? action.actionType : null,
        orgs: this.noticeOrgNames(action)
      }, extra)

      showToast(this, noticeOutcome(notice, ctx))
    },

    // The organisations a file-approved notice goes to (the owner, and the provider of
    // an RTB when it is not the owner): "{n} recipients at {orgs} are being e-mailed".
    noticeOrgNames (action) {
      var names = []

      if (!action) {
        return names
      }

      if (action.organisation) {
        names.push(action.organisation.shortName || action.organisation.name)
      }

      if (this.hasDistinctProvider()) {
        names.push(this.providerShort())
      }

      return names.filter(Boolean)
    },

    updateAction (value) {
      this.actionData = value
    },



    info (rowData, stepData, stepNo) {

      if (!rowData || !stepData || !stepNo) {
        return null
      }

      stepNo = Number(stepNo)

      // XOL-586 (F2): the recipients and the `sendMail` flag are the server's business now
      // (no recipient list, no `emails` here). What stays is the request's own context, which
      // `:upload-info` still hands to the file tables (their add-category gate).
      var data = {
        info: {}
      }

      if (rowData && rowData.state) {

        data.info.step = stepNo
        data.info.stepName = stepData.stepName
        data.info.rts_id = rowData.id ? Number(rowData.id) : null
        data.info.actionType = rowData.actionType
        data.info.requestType = (rowData.actionType === 'RTS' ? 'Share' : (rowData.actionType === 'RTU' ? 'Upgrade' : rowData.actionType === 'RTB' ? 'Backhaul' : 'Other'))
        data.info.site_id = rowData.site_id ? Number(rowData.site_id) : null
        data.info.towerCoName = this.xolasClient().name ? this.xolasClient().name : 'Xolas TMS'
        data.info.email = this.xolasClient().email ? this.xolasClient().email : 'support@xolas.io'
        data.info.phone = this.xolasClient().phone ? this.xolasClient().phone : null
        data.info.siteName = rowData.site && rowData.site.siteName ? rowData.site.siteName : 'No Site Selected'
        data.info.siteId = rowData.site && rowData.site.siteId ? rowData.site.siteId : 'N/A'
        data.info.telcoSiteId = rowData.site && rowData.site.telcoSiteId
        ? rowData.site.telcoSiteId
        : (rowData.state && rowData.state.step2 && rowData.state.step2.siteId ? rowData.state.step2.siteId : 'N/A')
        data.info.sharerName = rowData.organisation && rowData.organisation.name ? rowData.organisation.name : 'No Sharer Selected'
      }

//      console.log('info: data', data, 'rowData', rowData)


      return data
    },


    /**
     * Workflow Steps: find, getSibling & workflow
     */
    // XOL-596 (F4): finds the ONE object for a row; it never swaps one in. The
    // old `replace` / `step` arguments (replaceStepData) put a different object
    // at steps[i], which orphaned every alias to the row.
    findWorkflowStep (stepNo, partyNo, wf) {

      stepNo = Number(stepNo)
      partyNo = Number(partyNo)

      if (wf) {
        for (let i = 0; i < wf.length; i++) {
          if (Number(wf[i].partyNo) === partyNo && Number(wf[i].stepNo) === stepNo) {
            return wf[i]
          }
        }
      } else {
        this.$message({type: 'error', message: 'Workflow not found!'})
      }

      return null
    },


    workflow (partyNo, workflow) {

      partyNo = Number(partyNo)

      let wf = []

      if (partyNo > 0 && workflow && workflow.length > 0) {
        for (let i = 0; i < workflow.length; i++) {

          if (Number(workflow[i].partyNo) === partyNo) {
            wf.push({title: workflow[i].stepName, icon: workflow[i].stepIcon, colour: workflow[i].stepColour, comp: null, data: null})
          }

        }
      }

      return wf
    },



    getSibling (stepNo, stepId) {

      stepNo = Number(stepNo)
      stepId = Number(stepId)

      let list = this.rowData.steps

      if (list && stepNo && stepId) {
        for (let i = 0; i < list.length; i++) {
          if (Number(list[i].stepNo) === stepNo && Number(list[i].id) !== stepId) {
            return list[i]
          }
        }
      }
    },


    // XOL-526: null-safe org-id match. Guards against an unhydrated org()
    // (Number(undefined) → NaN → false, never a false-positive match) and
    // against string/number id mismatches from mixed API/JSON sources.
    requestOrgMatches (orgId) {
      var me = this.org()
      if (orgId === null || orgId === undefined || !me || me.id === null || me.id === undefined) return false
      return Number(orgId) === Number(me.id)
    },

    // XOL-526: is the logged-in org the RTx's organisation_id (owner)? Never
    // true for a Tower Co viewer — the Tower Co is party 1, not a counterparty.
    isRequestOwner () {
      var a = this.actionData || this.rowData
      return !this.isTowerCo() && !!a && this.requestOrgMatches(a.organisation_id)
    },

    // XOL-526: is the logged-in org the RTB's provider_id?
    isRequestProvider () {
      var a = this.actionData || this.rowData
      return !this.isTowerCo() && !!a && this.requestOrgMatches(a.provider_id)
    },

    // XOL-526: the provider, and NOT also the owner (an org can be both when it
    // is its own provider — treat that org as the owner, per plan §7).
    isProviderOnlyViewer () {
      return this.isRequestProvider() && !this.isRequestOwner()
    },

    // XOL-526: mirrors the API's tenant.towerco cross-tenant read rule — the
    // caller may read org-scoped data when it IS the Tower Co, or when the
    // data belongs to the caller's own org.
    canReadOrgScoped (orgId) {
      return this.isTowerCo() || this.requestOrgMatches(orgId)
    },

    // XOL-526: which party row (1 or 2) the CURRENT viewer's writes/reads
    // belong to. Tower Co is always party 1; every counterparty is party 2,
    // except a single-party workflow (parties < 2), where only pane 1 exists.
    viewerPartyNo () {
      var a = this.actionData || this.rowData
      return (this.isTowerCo() || !a || Number(a.parties) < 2) ? 1 : 2
    },

    // The party-1 row of step `stepNo` in the request this component shows, or null.
    // (Party-1 is authoritative: a manager's "Complete Step" toggle.)
    partyOneStepRow (stepNo) {
      var a = this.actionData || this.rowData
      var steps = a && a.steps
      if (!steps) return null
      for (var i = 0; i < steps.length; i++) {
        var s = steps[i]
        if (Number(s.stepNo) === Number(stepNo) && Number(s.partyNo) === 1) {
          return s
        }
      }
      return null
    },

    // XOL-526: moved verbatim from RequestWorkflow.partyStepCompleted
    // (XOL-107/XOL-344) so step1-5 and other request surfaces can gate on it too.
    // Missing party-1 row -> false (default allow, matches the API's own default).
    //
    // XOL-596 (completed steps, Carlos 2026-10-14): read from the BASELINE, what the
    // server last said, never the row's display value. The Complete Step tick writes
    // `step.completed` locally and holds it through the flush, the prompt and the
    // command; a gate on that value would grey the step the moment the box is ticked
    // and flash back on Cancel or a failed command. The baseline changes once, when
    // the server's answer is adopted. Reads `syncState.tick`, so a render that calls it
    // follows every adopt.
    partyOneStepCompleted (stepNo) {
      void sync.syncState.tick

      var row = this.partyOneStepRow(stepNo)

      return row ? !!sync.baselineValue(row, 'completed') : false
    },

    // XOL-596 (completed steps): is step `stepNo` FROZEN, i.e. read-only for everyone
    // until it is re-opened? Its party-1 row is completed (the baseline), on an
    // RTS / RTU / RTB / RTD request. PRG is excluded: it has no Complete Step and no
    // Re-open, so a completed PRG row would be stuck. Never gate on `step.stepNo` of a
    // transient `step`; pass a constant.
    isStepFrozen (stepNo) {
      var a = this.actionData || this.rowData

      return !!a && FROZEN_TYPES.indexOf(String(a.actionType || '').toUpperCase()) !== -1 && this.partyOneStepCompleted(stepNo)
    },

    // The Complete Step box's own gate (Tower Co manager who can write): who can re-open.
    canReopenStep () {
      return this.isTowerCo() && this.isManager() && this.canWriteStep()
    },

    // Every row of step `stepNo` (both parties) in this request.
    rowsOfStep (stepNo) {
      var a = this.actionData || this.rowData
      var steps = (a && a.steps) || []

      return steps.filter(function (s) { return Number(s.stepNo) === Number(stepNo) })
    },

    // The top-level `actionStepId` a request-step UI sends with a write that the API
    // can tie to a step (XOL-595 4.10): the id of the step-1 party-1 row for General
    // Remarks, else null. `rec.form` keeps Site -> Actions (ActionDetails) out.
    contextStepIdFor (rec, path) {
      if (!rec || rec.kind !== 'action' || sync.stepNoOfPath(rec, path) !== 1 || path !== 'remarks') {
        return null
      }

      var steps = (rec.row && rec.row.steps) || []

      for (var i = 0; i < steps.length; i++) {
        if (Number(steps[i].stepNo) === 1 && Number(steps[i].partyNo) === 1) {
          return steps[i].id
        }
      }

      return null
    },

    // The step-completed hint (CS-H) holds focus for a refusal that disabled the
    // control the user was in. Lives in RequestWorkflow, so it is found by id.
    focusFrozenHint () {
      var id = this.hintId || this.requestHintId

      this.$nextTick(function () {
        var el = typeof document !== 'undefined' ? document.getElementById(id) : null

        if (el && typeof el.focus === 'function') {
          el.focus()
        }
      })
    },

    // XOL-526: does a distinct backhaul provider exist on this RTB (i.e. is
    // provider_id set AND different from organisation_id)? Drives the shared
    // party-2 tab suffix, cue line and the EmailSetup provider line.
    hasDistinctProvider () {
      var a = this.actionData || this.rowData
      return !!(a && a.actionType === 'RTB' && a.provider_id &&
        Number(a.provider_id) !== Number(a.organisation_id))
    },

    // XOL-526: the provider's short name (A12's providerShortName, else the
    // viewer's own short name when the viewer IS the provider), or null when
    // neither resolves.
    providerShort () {
      var a = this.actionData || this.rowData
      if (a && a.providerShortName) return a.providerShortName
      if (this.isRequestProvider()) {
        var me = this.org()
        if (me && me.shortName) return me.shortName
      }
      return null
    },

    // XOL-526: providerShort(), with the UX spec's generic fallback.
    providerLabel () {
      var short = this.providerShort()
      return short || 'the backhaul provider for this request'
    },

    // XOL-526 (UX §2.4, §1.7): providerLabel(), capitalised for sentence-
    // initial use. A resolved short name is already properly cased, so this
    // is a no-op for it; it only matters for the generic fallback.
    providerLabelCapitalized () {
      var s = this.providerLabel()
      return s.charAt(0).toUpperCase() + s.slice(1)
    },

    // XOL-526 (UX §1.7(a)): the party-2 tab label, suffixed with the
    // provider's short name on an RTB where the provider is distinct from
    // the telco — "{TELCO} & {PROVIDER}" for every viewer. Party 1's label
    // is untouched. Moved here from RequestWorkflow/CompletedRequest, which
    // each had an identical copy, so it can be shared by every caller of
    // `partyName()` (a per-component method, still resolved on `this`).
    partyTabLabel (partyNo) {
      var base = this.partyName(partyNo)

      if (Number(partyNo) !== 2 || !this.hasDistinctProvider()) {
        return base
      }

      var short = this.providerShort()
      return short ? base + ' & ' + short : base
    },

    // XOL-526 (UX §1.7(b)): the Telco/Tower Co's lighter equivalent of the
    // provider banner, shown under the shared party-2 file list. Moved here
    // from RequestWorkflow/CompletedRequest, which each had an identical copy.
    sharedFilesCue () {
      var label = this.providerLabel()

      if (this.isRequestOwner()) {
        return 'Files on this tab are shared with ' + label + '. Both organisations can see and download every file here; each file shows who uploaded it.'
      }

      if (this.isRequestProvider()) {
        return 'Files on this tab are shared with ' + this.actionData.organisation.name + '. Both organisations can see and download every file here; each file shows who uploaded it.'
      }

      return 'Files on this tab are uploaded by ' + this.actionData.organisation.name + ' and ' + label + '. Each file shows who uploaded it.'
    },

    // XOL-526 (D12): a null-safe read of a `state.edits.<field>` stamp —
    // `{ by, org, at }` of whoever last changed this shared field, the Tower Co
    // included (XOL-595 / XOL-596 R2.21: the server stamps every writer of a
    // shared path, not only a counterparty). Returns null before the field was
    // first changed through the API.
    editStamp (state, field) {
      return (state && state.edits && state.edits[field]) ? state.edits[field] : null
    },

    // XOL-593 + XOL-596: is the decision this row DISPLAYS the one the server
    // recorded? The approval is a command now: the select changes the v-model at
    // once, but approved / approved_at / decided_at stay at the server's last word
    // until the response is adopted. In that window the row can still say
    // `approved` with the PREVIOUS decision's decided_at (step 5 counts a
    // Rejection as `approved` too), so a relative time here would belong to
    // another decision. Compare the displayed status with the baseline (what the
    // server last said); a row nobody registered has no baseline of its own and
    // reads as confirmed.
    decisionConfirmed (step) {
      if (!step) { return false }
      void sync.syncState.tick
      var base = sync.getBaseline(step)
      if (base.approvedStatus === undefined) { return true }
      return String(base.approvedStatus == null ? '' : base.approvedStatus) === String(step.approvedStatus == null ? '' : step.approvedStatus)
    },

    // XOL-593: the "Approved … ago" label for a step row. Reads the ADOPTED
    // decided_at; null (nothing rendered) while the decision on screen is not yet the
    // server's (a pick waiting for the pause, or saving).
    approvalWhen (step) {
      if (!step || !this.decisionConfirmed(step)) { return null }
      return this.formatDecisionAgo(step[STEP_DECIDED_AT], step.approved_at)
    },

    // XOL-596 (decision fields, D7): the step-2 header's approved flag. While the pick
    // is not the server's yet, the title shows the flag the API WILL set for it
    // (StepApprovalRules: step 5 is Approved or Rejected, every other row Approved), so
    // the header never shows the new word with the old icon. Once it is confirmed, the
    // row's own flag.
    displayApproved (step) {
      if (!step) { return false }
      if (this.decisionConfirmed(step)) { return !!step.approved }
      var status = step.approvedStatus
      if (this.approvalKind(step) === 'step5') {
        return status === 'Approved' || status === 'Rejected'
      }
      return status === 'Approved'
    },

    // XOL-593: hover text for the "Approved … ago" label — the absolute moment
    // the decision was recorded. Null (no title attribute) on legacy rows: the
    // day label already says all we know, and approved_at's 00:00 is not a
    // real time.
    approvalWhenTitle (step) {
      if (!step || !this.decisionConfirmed(step)) { return null }
      var at = this.formatDateTime(step[STEP_DECIDED_AT])
      return at ? 'Approval recorded ' + at : null
    },

    // XOL-526 (UX §2.4, U-I): "Last updated by {who} ({org}) on {when}" from
    // a D12 stamp, or '' when the field has never been counterparty-edited
    // (never call formatDateTime on a null stamp).
    lastUpdatedLine (stamp) {
      if (!stamp || !stamp.by) return ''
      return 'Last updated by ' + stamp.by + (stamp.org ? ' (' + stamp.org + ')' : '') + ' on ' + this.formatDateTime(stamp.at)
    },

    // XOL-526 (UX §2.4, U-I): the "someone else can also …" note shown above
    // a shared editor, while it is actually editable for this viewer. `action`
    // is the field-specific verb phrase, e.g. 'edit these remarks' or
    // 'change the Technical Proposal type'. The owner sees the provider named
    // (capitalised — it may open the sentence); the provider sees the
    // telco's real org name.
    // XOL-534 round 3: the UX spec (§2.4) says this note shows only when the
    // field is editable for THIS viewer under isCounterpartyWriter() — i.e.
    // never for the Tower Co. That guard is centralised HERE, not left to
    // each caller, after step2's tpTypeNote proved a caller can omit it:
    // `counterpartyTpLocked` is false for a writable Tower Co too (its
    // canWriteOwnStep() is canWriteStep()), so a per-caller "is this field
    // unlocked" check alone doesn't distinguish "unlocked for a Tower Co"
    // from "unlocked for a counterparty". hasDistinctProvider() is
    // centralised too — the note text always names a specific counterparty,
    // so it's meaningless without one, and every caller already needed it.
    sharedFieldNote (action) {
      if (!this.isCounterpartyWriter() || !this.hasDistinctProvider()) return null

      return copyText('requestSync.sharedNote', { who: this.sharedFieldWho(), action: action })
    },

    // The other counterparty, named for this viewer: the owner sees the provider
    // (capitalised, it may open the sentence), the provider sees the telco.
    sharedFieldWho () {
      var a = this.actionData || this.rowData

      return this.isRequestOwner()
        ? this.providerLabelCapitalized()
        : ((a && a.organisation && a.organisation.name) ? a.organisation.name : 'The other organisation')
    },

    // XOL-596 (UX item 17): General Remarks is the one shared field the Tower Co
    // can edit too (ActionDetails), so a counterparty's note always names the
    // Tower Co, whether or not there is a distinct provider.
    sharedGeneralRemarksNote () {
      if (!this.isCounterpartyWriter()) return null

      return copyText('requestSync.sharedNote.generalRemarks', {
        who: this.hasDistinctProvider() ? this.sharedFieldWho() : null,
        towerCo: this.towerCoName()
      })
    },

    // A step component unmounting with a text field focused gets no blur: release
    // every focus in this drawer's form so nothing stays held for good.
    releaseFocused () {
      sync.releaseFocusedInScope(this.syncScope || this.requestSyncScope)
    },

    // XOL-526 (D9): a non-Tower-Co, non-view-only caller who is either the
    // owner or the provider of an ACTIVE RTB. RTB-only, mirrors API A9/A10
    // (RequestPartyResolver::canWriteContent, xolas-tms-api xol-531 @
    // 1c835715). Flipped on in SPA-2 (XOL-534) now API-4/5 are live.
    isCounterpartyWriter () {
      var a = this.actionData || this.rowData
      return !this.isTowerCo() && !this.isViewOnly() && !!a && a.actionType === 'RTB' &&
        this.isActive(a) && (this.isRequestOwner() || this.isRequestProvider())
    },

    // XOL-526: request-level (PUT /actions) write gate — Tower Co keeps its
    // existing canWriteStep() rule; a counterparty needs isCounterpartyWriter().
    canWriteOwnRequest () {
      return this.isTowerCo() ? this.canWriteStep() : this.isCounterpartyWriter()
    },

    // XOL-526: step-level (PUT /step) write gate for a SPECIFIC step row.
    // A counterparty may only write its own party-2 row, and only while the
    // matching party-1 row isn't completed (mirrors API A10).
    canWriteOwnStep (step) {
      // XOL-596 (completed steps): a completed step is read-only for the Tower Co too.
      if (this.isTowerCo()) return this.canWriteStep() && !(step && this.isStepFrozen(step.stepNo))
      return this.isCounterpartyWriter() && !!step && Number(step.partyNo) === 2 &&
        !this.partyOneStepCompleted(step.stepNo)
    },

    // XOL-348: return the organisation_id of the party-1 step (the owning
    // TowerCo step). Returns null when steps are unavailable — null → no write,
    // which is the safe direction.
    requestOwnerOrgId () {
      var data = (this.actionData || this.rowData)
      var steps = data && data.steps
      if (!steps) return null
      for (var i = 0; i < steps.length; i++) {
        var s = steps[i]
        if (Number(s.partyNo) === 1) {
          return s.organisation_id != null ? s.organisation_id : null
        }
      }
      return null
    },

    // XOL-348: true only when the logged-in client owns the request.
    // Null-guards xolasClient() so an unhydrated client never throws
    // (Number(undefined) → NaN → false).
    ownsRequest () {
      var ownerId = this.requestOwnerOrgId()
      if (ownerId === null) return false
      var client = this.xolasClient()
      return Number(ownerId) === Number(client && client.id)
    },

    canWriteStep () {
      // XOL-371 review 2026-07-21: any non-view-only Tower Co user may write
      // steps; the XOL-348/362 manager/approver/ownership gating is dropped
      // (server enforces role:towerco + viewonly.gate).
      return this.isTowerCo() && !this.isViewOnly()
    },

    findStep (id, callback) {
      this.fetchData('step/' + id, callback, 'Request Step')
    },



    /* ====================================================================
       XOL-596: per-field, versioned saves (plan R2.5-R2.12, R2.22, R2.23)

       The state machine lives in lib/requestRowSync; this block is the part
       that needs a component: the HTTP (`send`), the endpoints, the viewer's
       identity for the path-ownership table and the local rows. Templates call
       holdField / releaseField / fieldChanged; saving happens at the form's
       3 s pause or at a flush point, never from a template.

       A component under a drawer finds its form scope in `this.syncScope`
       (RequestWorkflow provides it, children inject it; slice 2). Without one a
       row has no pause timer and is only saved by an explicit flush.
       ==================================================================== */

    // The ONE object for a row is registered once, on load, before any
    // auto-fill that should count as an edit. Idempotent.
    syncRegisterRow (row, kind, opts) {
      var self = this
      var o = opts || {}

      return sync.registerRow(row, kind, {
        scope: o.scope || this.syncScope || this.requestSyncScope || null,
        // An EditPane form ('actionDetails' | 'progressStep') names its own row in notices.
        form: o.form,
        sender: function (req) {
          return self.sendFieldSave(req)
        },
        // A decision group (approval controls) is sent as ONE approval command.
        decisionSender: function (req) {
          return self.sendDecisionSave(req)
        }
      })
    },

    // A row must be registered on load, before its first edit. A late
    // registration baselines from the row as it is NOW, so that first change
    // reads as clean and is never saved.
    warnUnregistered (row, fn, path) {
      if (!sync.getRecord(row)) {
        console.error('XOL-596: ' + fn + '(' + path + ') on a row that was never registered; its first change will not be saved. Call syncRegisterRow on load.')
      }
    },

    // Make sure a row has a record that knows how to send.
    syncEnsure (row, kind) {
      var rec = sync.getRecord(row)
      var scope = this.syncScope || this.requestSyncScope

      // A row this drawer's scope does not list yet (a step row that arrived after
      // load) joins it, so the drawer's close flushes it.
      if (!rec || !rec.sender || (scope && scope.rows.indexOf(rec) === -1)) {
        rec = this.syncRegisterRow(row, kind)
      }

      return rec
    },

    // `@focus` of a text-type input (HtmlInput, TextInput, raw el-input).
    holdField (row, path) {
      this.warnUnregistered(row, 'holdField', path)
      this.syncEnsure(row)
      return sync.holdField(row, path)
    },

    // `@blur`. Never saves.
    releaseField (row, path) {
      return sync.releaseField(row, path)
    },

    // Every content-field input/change handler and list button, AFTER the model
    // changed. Marks the field changed and restarts the form's pause.
    fieldChanged (row, path) {
      this.warnUnregistered(row, 'fieldChanged', path)
      this.syncEnsure(row)
      return sync.fieldChanged(row, path)
    },

    // A read-only control (a locked or completed approval) reached its change handler:
    // put the server's last value back on the field. Records nothing, sends nothing.
    restoreServerValue (row, path) {
      return sync.restoreServerValue(row, path)
    },

    // A template that edited an OBJECT (`state`): mark each changed LEAF changed
    // (XOL-595 A4: never send a parent of a protected path). Returns the paths.
    fieldsChangedUnder (row, basePath) {
      this.syncEnsure(row)
      return sync.fieldsChangedUnder(row, basePath)
    },

    // Record what the user did to a list (add / remove by key) so a 409 on a
    // recipient list can re-apply it onto the server's list (R2.9).
    recordListOp (row, path, op) {
      this.syncEnsure(row)
      return sync.recordListOp(row, path, op)
    },

    // Reactive helpers for templates (the cue slot's `cue` / `dirty` props).
    fieldCue (row, path) {
      return sync.fieldCue(row, path)
    },

    fieldDirty (row, path) {
      return sync.fieldDirty(row, path)
    },

    fieldHeld (row, path) {
      return sync.fieldHeld(row, path)
    },

    // "Use their version" (UF11).
    takeServerValue (row, path) {
      return sync.takeServerValue(row, path)
    },

    // Who the viewer is, for the path-ownership table (guard G-OWN).
    rowSyncCtx (rec) {
      var a = this.actionData || this.rowData || (rec.kind === 'action' ? rec.row : null)

      return {
        isTowerCo: this.isTowerCo(),
        actionType: a ? a.actionType : undefined,
        isRequestOwner: this.isRequestOwner(),
        isRequestProvider: this.isRequestProvider(),
        partyNo: rec.kind === 'step' ? rec.row.partyNo : null,
        telcoEquipmentAllowed: this.canTelcoWriteEquipment()
      }
    },

    // R2.20: may the request owner (a telco) write actions
    // `state.step2.equipment`? RTS only; the Tower Co must have ticked "Allow
    // Telcos to Input Equipment" on step 2 party 1; and that step is neither
    // approved nor completed. Reads the PARTY-1 row (the telco's own step is the
    // party-2 row, which never carries the flag).
    canTelcoWriteEquipment () {
      var a = this.actionData || this.rowData

      if (!a || a.actionType !== 'RTS' || !this.isRequestOwner() || this.isViewOnly() || !this.isActive(a)) {
        return false
      }

      var p1 = null

      for (var i = 0; a.steps && i < a.steps.length; i++) {
        if (Number(a.steps[i].stepNo) === 2 && Number(a.steps[i].partyNo) === 1) {
          p1 = a.steps[i]
        }
      }

      return !!(p1 && p1.state && p1.state.allowClientEquipmentInput && !p1.approved && !this.partyOneStepCompleted(2))
    },

    // XOL-596: a row with no id must never produce a request (steps are only
    // created server-side by createWorkflow; an id-less row is a bug).
    rowHasId (rec) {
      return rec.id !== undefined && rec.id !== null && rec.id !== ''
    },

    // The v3 request for one field: { url, body } or null when the row has no
    // id, the path may not be sent by this viewer, or the row has no version.
    // The body comes from lib/requestRowSync.buildFieldBody, the only place
    // `base` is computed.
    fieldRequest (rec, path, value) {
      if (!this.rowHasId(rec)) {
        return null
      }

      var own = sync.checkPathOwnership(this.rowSyncCtx(rec), rec.kind, path)
      var body = own.ok ? sync.buildFieldBody(rec, path, value) : null

      if (!body) {
        return null
      }

      // General Remarks saved from the request workflow is step 1's (Carlos, 2026-10-14):
      // the step context lets the API refuse it while step 1 is completed. Only the
      // `remarks` path carries it, never an EmailSetup / sendMail save.
      var contextStepId = this.contextStepIdFor(rec, path)

      if (contextStepId !== null) {
        body.actionStepId = contextStepId
      }

      return { url: this.getEndpoint(rec.kind === 'step' ? '/step' : '/actions', rec.id), body: body, session: this.editSessionOf(rec) }
    },

    // XOL-586 delta 2: the edit session of a row's save, the id of the form scope it belongs
    // to (`createScope` gives each one; one request drawer opening = one scope = one session,
    // so two tabs are two). Sent as the `X-Edit-Session` header so the API's Activity log
    // merges edits within a session only. A header, not a body key: FieldPatch refuses unknown
    // body keys, an older API ignores a header. null when the row has no scope.
    editSessionOf (rec) {
      return rec && rec.scope && rec.scope.editSession ? rec.scope.editSession : null
    },

    // The `sender` registered for every row: one queued field save. Runs inside
    // the row's queue; resolves an OUTCOME, never rejects.
    //   { status, path, kind, id, value, ... } with status one of
    //   saved | stale | closed | locked | read-only | no-version | failed |
    //   network | blocked | version-required
    sendFieldSave (req) {
      var self = this
      var rec = req.rec
      var path = req.path

      var done = function (status, extra) {
        return extend({ status: status, path: path, kind: rec.kind, id: rec.id, value: req.value }, extra)
      }

      // After a 428 the page is out of date: nothing is sent, edits stay on screen.
      if (sync.syncState.versionRequiredSeen) {
        return Promise.resolve(done('version-required'))
      }

      // A row with no id has no URL: PUT /step with no id is not a request this
      // app may make. Send nothing, re-GET the action and take its values, and
      // treat the edit as lost.
      if (!this.rowHasId(rec)) {
        console.error('XOL-596: row has no id, not sending', rec.queueKey, path)
        return this.recoverFromServer(rec, req, done('no-id'), null)
      }

      // A row with no version cannot be saved (B1). Re-GET, take the server's,
      // and treat the edit as lost.
      if (typeof req.contentVersion !== 'number' || typeof req.base !== 'number') {
        console.error('XOL-596: no contentVersion for', rec.queueKey, '- not sending', path)
        return this.recoverFromServer(rec, req, done('no-version'), null)
      }

      var own = sync.checkPathOwnership(this.rowSyncCtx(rec), rec.kind, path)

      if (!own.ok) {
        console.error('XOL-596: path not allowed for this viewer, not sending', rec.queueKey, path, own.reason)
        return Promise.resolve(done('blocked', { reason: own.reason }))
      }

      var request = this.fieldRequest(rec, path, req.value)

      if (!request) {
        console.error('XOL-596: no request could be built', rec.queueKey, path)
        return Promise.resolve(done('blocked', { reason: 'no-request' }))
      }

      // The page is being closed or reloaded (R2.18 B): the same body, sent with
      // fetch keepalive so it can outlive the page. Its answer, if the page stays,
      // is handled exactly like an axios one.
      if (req.keepalive) {
        return keepalivePut(request.url, request.body, request.session ? { 'X-Edit-Session': request.session } : null).then(function (res) {
          return self.fieldSaveSucceeded(rec, req, res.data, done)
        }, function (err) {
          if (err && err.response && err.response.status === 428) {
            sync.promptReload(self, { url: request.url })
          }

          return self.fieldSaveFailed(rec, req, err, done)
        }).catch(function (e) {
          console.error('XOL-596: handling a keepalive save threw', rec.queueKey, path, e)

          return done('failed', { err: e })
        })
      }

      return new Promise(function (resolve) {
        var settled = false
        var called = false
        var finish = function (outcome) {
          if (!settled) {
            settled = true
            resolve(outcome)
          }
        }

        // Quiet: send() raises no toast of its own, the burst reports. A save is
        // never `saveForm` (it injects updated_by, a 422 under v3).
        self.send(self, request.url, request.body, rec.kind === 'step' ? (rec.row.stepName || 'Request step') : 'Request',
          function (data) {
            called = true
            finish(self.fieldSaveSucceeded(rec, req, data, done))
          },
          function (err) {
            called = true
            self.fieldSaveFailed(rec, req, err, done).then(finish, function () {
              finish(done('failed'))
            })
          },
          request.session
            ? function (url, data) { return axios.put(url, data, { headers: { 'X-Edit-Session': request.session } }) }
            : axios.put,
          false, 'Save', null, true, false, true
        ).then(function () {
          // send() resolves once its callbacks ran. A failure outcome may still
          // be on its way (a recovery re-GET); only when NEITHER callback ran
          // (a non-200 success, say) is the save simply not saved.
          if (!called) {
            finish(done('failed'))
          }
        }, function (e) {
          // The failure callback runs inside send()'s own .catch, so a throw in
          // it (or in adopt / recovery) REJECTS send()'s promise. Without this the
          // save never settles, the row's queue stalls, and every flush point
          // waits forever.
          console.error('XOL-596: handling a field save threw', rec.queueKey, path, e)
          finish(done('failed', { err: e }))
        })
      })
    },

    fieldSaveSucceeded (rec, req, data, done) {
      this.adoptSaveResponse(data, { seq: req.seq, row: rec.row, kind: rec.kind })

      var saved = data ? (rec.kind === 'step' ? data.step : data.action) : null

      // Does the returned row hold the value we sent, and where does the field's
      // base go? (G-BASE: another user may have written the path between our
      // commit and the API's response snapshot.)
      var c = sync.confirmSaved(rec, req.path, req.value, saved)

      return done('saved', { version: c.version, confirmed: c.confirmed })
    },

    // Classify a failed save by HTTP status plus `code` (never `error`).
    fieldSaveFailed (rec, req, err, done) {
      var response = err && err.response
      var data = (response && response.data && typeof response.data === 'object') ? response.data : {}
      var status = response ? response.status : null
      var code = typeof data.code === 'string' ? data.code : null

      // send()'s view-only guard calls failCallback with nothing.
      if (err === undefined || err === null) {
        return Promise.resolve(done('blocked', { reason: 'view-only' }))
      }

      // No response: a network error or timeout, after axios-retry. The field
      // stays dirty, so the next pause-flush or flush point retries it.
      if (!response || status >= 500 || status === 401) {
        return Promise.resolve(done('network', { err: err }))
      }

      // send() already prompted the reload; the edit stays on screen.
      if (status === 428) {
        return Promise.resolve(done('version-required', { code: code }))
      }

      if (status === 409 && code === 'request_closed') {
        return this.recoverFromServer(rec, req, done('closed', { code: code }), data)
      }

      if (status === 409 && code === 'stale_field' && !req.reapplied && RECIPIENT_PATHS.indexOf(req.path) !== -1 &&
        sync.getListOps(rec, req.path).length > 0) {
        // A recipient list: re-apply what the user did onto the server's list and
        // resubmit ONCE (R2.9). A second conflict is the ordinary stale case.
        return this.reapplyRecipients(rec, req, err, done)
      }

      if (status === 409) {
        // stale_field, or any other 409 on a content save: the server wins.
        return Promise.resolve(this.onStaleSave(rec, req, err, done))
      }

      // 422 read_only_fields or invalid_value (XOL-595 A1): a bug signal either
      // way. `code` and `fields` ride on the outcome; the notice is the same.
      if (status === 422) {
        console.error('XOL-596: save refused as read-only', rec.queueKey, req.path, data.fields)
        return this.recoverFromServer(rec, req, done('read-only', { code: code, fields: data.fields, requestId: this.requestIdOf(err) }), null)
      }

      // 403: step_locked (the counterparty completed-step and TP-approved locks,
      // the telco equipment flag / completed / approved locks) or
      // equipment_row_locked. The lock notice is picked from the re-GET state
      // (UX item 3), so `code` rides on the outcome.
      if (status === 403) {
        // approval_locked (XOL-595 4.9) carries the rows (who locked it, and when):
        // adopt them instead of a re-GET.
        //
        // Adopted with a seq taken NOW, after the answer (as refetchAfterRefusal does):
        // a command of ours may have been answered since this save was issued, which
        // raises the row's workflow floor above `req.seq`, and an adopt older than the
        // floor skips the owned keys, the lock keys among them.
        //
        // step_completed (XOL-596 completed steps) carries the rows too (the completed
        // party-1 row says who and when) and is adopted the same way, with no re-GET.
        var withRows = code === 'approval_locked' || code === 'step_completed'

        return this.recoverFromServer(rec, req, done('locked', { code: code, message: data.message || data.error, stepNo: data.stepNo }),
          withRows ? data : null, withRows ? sync.nextSeq() : undefined)
      }

      return this.recoverFromServer(rec, req, done('failed', { code: code, httpStatus: status, message: data.message || data.error }), null)
    },

    requestIdOf (err) {
      var h = err && err.config && err.config.headers

      return (h && h['X-Request-Id']) || null
    },

    // R2.9: someone else changed the same recipient list. Their list
    // (`conflicts[].current`) plus this user's recorded ops (add / set / remove, by
    // `recipientKey`: `c:<contact id>` or `e:<address>`) is resubmitted once, with the
    // new base: a deliberate re-apply, never a blind retry. Resolves the outcome of
    // that second request.
    reapplyRecipients (rec, req, err, done) {
      var data = (err && err.response && err.response.data) || {}
      var conflicts = Array.isArray(data.conflicts) ? data.conflicts : []
      var conflict = null

      for (var i = 0; i < conflicts.length; i++) {
        if (conflicts[i] && conflicts[i].path === req.path) {
          conflict = conflicts[i]
        }
      }

      // Their list, and the version it reflects. The 409 body's own
      // `contentVersion` is read after the rollback, outside the lock, so it can
      // be newer than `current`: a base taken from it would skip a write in
      // between. `changedAtVersion` is the version `current` is at (G-BASE).
      var base = conflict ? Number(conflict.changedAtVersion) : NaN

      if (!conflict || !Array.isArray(conflict.current) || conflict.changedAtVersion === undefined ||
        conflict.changedAtVersion === null || isNaN(base)) {
        return Promise.resolve(this.onStaleSave(rec, req, err, done))
      }

      var theirs = applyOps(sync.cloneValue(conflict.current), sync.getListOps(rec, req.path))

      // Take the server's rows (the held list keeps the user's value), then show
      // the merged list and send it with a base that has seen their change.
      this.adoptSaveResponse(data, { seq: req.seq, row: rec.row, kind: rec.kind })

      var entry = rec.entries[req.path]

      // In place: the components alias the list (`emails.owner.additional`).
      sync.assignPath(rec.row, req.path, theirs)

      if (entry) {
        entry.baseVersion = base
        entry.cue = null
      }

      var again = extend({}, req)

      again.value = sync.cloneValue(theirs)
      again.base = base
      again.contentVersion = rec.lastAdoptedVersion
      again.seq = sync.nextSeq()
      again.reapplied = true

      if (entry) {
        entry.inFlightSeq = again.seq
        entry.inFlightValue = sync.cloneValue(theirs)
      }

      return this.sendFieldSave(again).then(function (out) {
        if (out && out.status === 'saved') {
          out.sentValue = sync.cloneValue(theirs)
          out.reapplied = true
        }

        return out
      })
    },

    // 409 stale_field (R2.5): the path takes the server's value, the entry is
    // dropped, the user's value goes to the stash, and every OTHER held field
    // keeps its value and its own base (the adopt rule). Never retried.
    onStaleSave (rec, req, err, done) {
      var data = (err && err.response && err.response.data) || {}
      var conflicts = Array.isArray(data.conflicts) ? data.conflicts : []
      var conflict = null

      for (var i = 0; i < conflicts.length; i++) {
        if (conflicts[i] && conflicts[i].path === req.path) {
          conflict = conflicts[i]
        }
      }

      if (!conflict && conflicts.length > 0) {
        conflict = conflicts[0]
      }

      this.adoptSaveResponse(data, { seq: req.seq, row: rec.row, kind: rec.kind })

      var current = conflict ? conflict.current : undefined

      // What the user has NOW, not what was sent: they may have typed on while the
      // save was in flight, and all of it is lost to the server's value.
      var latest = sync.cloneValue(sync.getPath(rec.row, req.path))

      sync.takeServer(rec, req.path, current)

      var serverValue = current !== undefined ? current : sync.getPath(rec.row, req.path)
      var item = sync.stashLost({
        rowKind: rec.kind,
        rowId: rec.id,
        actionType: rec.kind === 'action' ? rec.row.actionType : (this.actionData || {}).actionType,
        stepNo: rec.kind === 'step' ? rec.row.stepNo : null,
        partyNo: rec.kind === 'step' ? rec.row.partyNo : null,
        path: req.path,
        yours: latest,
        serverValue: serverValue,
        reason: data.code || 'stale_field',
        by: conflict ? conflict.by || null : null,
        org: conflict ? conflict.org || null : null,
        at: conflict ? conflict.at || null : null,
        changedAtVersion: conflict ? conflict.changedAtVersion : undefined,
        burstId: req.burst ? req.burst.id : null,
        meta: sync.rowMeta(rec)
      })

      return done('stale', {
        value: latest,
        code: data.code || 'stale_field',
        conflicts: conflicts,
        serverValue: serverValue,
        by: item.by,
        org: item.org,
        at: item.at,
        stashId: item.stashId
      })
    },

    // 403 locks, 422, no version, an unknown 4xx, request_closed: adopt what the
    // server holds (the response body when it carries rows, else a re-GET of the
    // action), drop the field's entry so it is not pending any more, and stash
    // the user's value so the notice can list it.
    recoverFromServer (rec, req, outcome, body, adoptSeq) {
      var adopted

      if (body && (body.action || body.step)) {
        this.adoptSaveResponse(body, { seq: adoptSeq === undefined ? req.seq : adoptSeq, row: rec.row, kind: rec.kind })
        adopted = Promise.resolve(true)
      } else {
        adopted = this.refetchAction(rec)
      }

      return adopted.then(function () {
        var deferred = rec.deferred[req.path]
        // What the user has NOW (they may have typed on during the flight).
        var latest = sync.cloneValue(sync.getPath(rec.row, req.path))

        sync.takeServer(rec, req.path, undefined)

        var item = sync.stashLost({
          rowKind: rec.kind,
          rowId: rec.id,
          path: req.path,
          yours: latest,
          serverValue: deferred ? deferred.value : sync.getPath(rec.row, req.path),
          reason: outcome.code || outcome.status,
          burstId: req.burst ? req.burst.id : null,
          meta: sync.rowMeta(rec)
        })

        outcome.value = latest
        outcome.serverValue = item.serverValue
        outcome.stashId = item.stashId

        return outcome
      }).catch(function (e) {
        console.error('XOL-596: recoverFromServer failed', e)
        return extend(outcome, { recovered: false })
      })
    },

    // GET /actions/{id} (the full row), adopted with a seq taken BEFORE the
    // request, so a response that overtakes a later write is ignored. Resolves
    // the action, or null when the GET failed.
    refetchActionById (actionId, target) {
      return this.refetchActionResult(actionId, target).then(function (res) {
        return res.data
      })
    },

    // As refetchActionById, but says WHY it failed: `gone` is true for a 404 (the
    // action no longer exists). Resolves { data, gone }.
    refetchActionResult (actionId, target) {
      var self = this

      if (!actionId) {
        return Promise.resolve({ data: null, gone: false })
      }

      var seq = sync.nextSeq()

      return axios.get(this.getEndpoint('/actions/' + actionId)).then(function (response) {
        var data = response.data

        if (data && data.data) {
          data = data.data
        }

        var into = target || self.actionData || self.rowData

        if (data && into) {
          self.adoptAction(data, { seq: seq, target: into })
        }

        return { data: data || null, gone: false }
      }).catch(function (e) {
        console.error('XOL-596: refetch of action', actionId, 'failed', e)
        return { data: null, gone: !!(e && e.response && e.response.status === 404) }
      })
    },

    refetchAction (rec) {
      return this.refetchActionById(
        rec.kind === 'action' ? rec.id : rec.row.action_id,
        rec.kind === 'action' ? rec.row : null)
    },

    // GET step/{id}, adopted IN PLACE into `target` (the one object for that
    // row; F4), with a seq taken when the request is ISSUED, so a late GET never
    // reverts a newer write. Replaces the old `this.step = data` swap in the step
    // components' setStep. `callback(target)` runs after a successful adopt.
    loadStep (id, target, callback) {
      var self = this
      var seq = sync.nextSeq()

      this.findStep(id, function (data) {
        if (!data || !target) {
          return
        }

        self.adoptStepRow(target, data, { seq: seq })

        if (typeof callback === 'function') {
          callback(target)
        }
      })
    },

    // Adopt a server STEP row into the local object for that row, in place.
    // `ctx = { seq }`. See lib/requestRowSync.adoptRow (G-MERGE, per-field hold).
    adoptStepRow (target, serverRow, ctx) {
      return sync.adoptRow(target, serverRow, { kind: 'step', seq: ctx ? ctx.seq : undefined, command: !!(ctx && ctx.command) })
    },

    // Adopt a server ACTION (the lean `{...columns, steps[]}`), merging every
    // `steps[]` row by id. `ctx = { seq, target }`; target defaults to the
    // component's actionData / rowData.
    adoptAction (serverAction, ctx) {
      var target = (ctx && ctx.target) || this.actionData || this.rowData

      return sync.adoptRow(target, serverAction, { kind: 'action', seq: ctx ? ctx.seq : undefined, command: !!(ctx && ctx.command) })
    },

    // A save or command response: `{step, action}` (/step and commands) or
    // `{action}` (/actions). Adopts the acted-on step first, then the action
    // with every steps[] row. `ctx = { seq, row, kind }`: the row the request
    // was about, so it is adopted into the object the component actually holds.
    adoptSaveResponse (data, ctx) {
      if (!data || typeof data !== 'object') {
        return
      }

      var c = ctx || {}
      var action = c.kind === 'action' && c.row ? c.row : (this.actionData || this.rowData)

      if (data.step) {
        var local = null

        if (c.kind === 'step' && c.row && Number(c.row.id) === Number(data.step.id)) {
          local = c.row
        } else if (action && Array.isArray(action.steps)) {
          for (var i = 0; i < action.steps.length; i++) {
            if (Number(action.steps[i].id) === Number(data.step.id)) {
              local = action.steps[i]
            }
          }
        }

        if (local) {
          this.adoptStepRow(local, data.step, { seq: c.seq, command: c.command })
        }
      }

      if (data.action && action) {
        this.adoptAction(data.action, { seq: c.seq, target: action, command: c.command })
      }
    },

    // Save every changed field of the given paths as ONE burst (EditPane Save,
    // system saves). Resolves { ok, results, failed, burst }.
    flushFieldPaths (items, opts) {
      for (var i = 0; i < items.length; i++) {
        this.syncEnsure(items[i].row, items[i].kind)
      }

      return sync.flushPaths(items, opts || {})
    },

    // One path. Resolves its outcome ({status: 'skipped'} when it did not change).
    flushFieldPath (row, path, opts) {
      return this.flushFieldPaths([{ row: row, path: path }], opts).then(function (res) {
        return res.results[0] || { status: 'skipped', path: path }
      })
    },

    // Pause-flush this form now: what the 3 s pause timer does. No component calls it;
    // the specs do, through `helpers/decisionFlush.saveDecisions` and directly in
    // `RequestMixins.spec.js` and `requests/fieldBase.spec.js`, to run the pause without
    // waiting. Kept for that (the real flush points use `sync.flushScope*`).
    flushForm (opts) {
      var scope = (opts && opts.scope) || this.syncScope || this.requestSyncScope

      if (!scope) {
        return Promise.resolve({ ok: true, results: [], failed: [], burst: null })
      }

      return sync.flushScope(scope, opts || {})
    },

    // Flush the given rows now, whatever form they belong to.
    flushRows (rows, opts) {
      return sync.flushRows(rows, opts || {})
    },

    // The ordering rule for an e-mail (XOL-582): the remarks are saved BEFORE
    // they are e-mailed. The form pause is cut short and the rows' changed fields
    // are flushed; a flush that hangs asks the user (Keep waiting / Cancel).
    // Resolves true when the e-mail may go: every save returned 2xx. A failed save
    // has already raised its notice, with the "e-mail wasn't sent" footer.
    flushBeforeSend (rows, ordering) {
      return waitForSaves(this.$root, sync.flushRows(rows, { reason: 'command', ordering: ordering || { kind: 'email' } }), {
        leaveLabel: copyText('requestSync.hang.cancel')
      }).then(function (waited) {
        return !waited.left && !!waited.result && !!waited.result.ok
      })
    },

    // XOL-586 (F2): every request e-mail is sent by the API. The button only asks
    // for it: POST /actions/{id}/notify { event, ...extra, idempotencyKey }, where
    // `event` is 'test' (extra { party }), 'approval_notice' (extra { stepId }) or, for an RTA
    // (XOL-67), 'rta_decision' (no step: the Tower Co decision). No recipient and no content leaves
    // the browser, but a Re-send's picked `recipients` and the `previewVersion` they were picked from.
    //
    // ONE idempotencyKey per click: axios-retry re-sends a POST on a 5xx, and the API
    // answers a replayed key with the original batch (`duplicate: true`), so a retry
    // never mails twice. The result is shown as exactly one toast (a handled 4xx must
    // not also raise the generic "Operation Failed to Complete"; this does not go
    // through send()). Resolves { ok, status, code?, data?, toast } and never rejects.
    notifyRequest (event, extra) {
      var self = this
      var action = this.actionData || this.rowData

      if (!action || !action.id || !this.canWriteStep()) {
        return Promise.resolve({ ok: false, status: 'blocked' })
      }

      // Delta 1 (item 1): with Send E-mails off the approval e-mail is not asked for at all
      // (the button is disabled; this is the backstop) and nothing is said. A test still goes.
      // The RTA's decision e-mail is the same kind of e-mail (a button, off with Send E-mails).
      var approval = event === 'approval_notice' || event === 'rta_decision'

      if (approval && this.requestMailOff()) {
        return Promise.resolve({ ok: false, status: 'blocked', code: 'mail_off', toast: null })
      }

      var body = extend({ event: event, idempotencyKey: generateRequestId() }, extra || {})
      var ctx = this.notifyContext(action, extra, event)
      var settle = function (result) {
        result.toast = notifyOutcome(event, ctx, result)
        self.showMailToast(result.toast)

        // An approval e-mail that left a batch behind (a 200, and the 422s that commit one)
        // changes the step row's `approvalMail` (an RTA's `state.rta.tc.decisionMail`), which the
        // response does not carry: re-read the request, so the Send / Re-send label and the
        // last-sent line show the new batch.
        if (approval && result.data && result.data.batch_id) {
          return self.refetchActionById(action.id).then(function () { return result }, function () { return result })
        }

        // Delta 1 (item 1): a 422 `mail_off` means another user turned Send E-mails off since
        // this tab loaded the request. No toast: re-read the request, so the banner appears and
        // the button disables.
        if (approval && result.status === 422 && result.code === 'mail_off') {
          return self.refetchActionById(action.id).then(function () { return result }, function () { return result })
        }

        return result
      }

      return axios.post(this.getEndpoint('/actions/' + action.id + '/notify'), body).then(function (response) {
        return settle({ ok: true, status: response.status, data: response.data })
      }, function (err) {
        var response = err && err.response
        var data = (response && response.data && typeof response.data === 'object') ? response.data : {}

        console.error('XOL-586: notify failed', event, response ? response.status : err)

        return settle({
          ok: false,
          status: response ? response.status : null,
          code: typeof data.code === 'string' ? data.code : null,
          data: data,
          message: data.message || data.error || null
        })
      })
    },

    // Delta 1 (item 1): is Send E-mails off for this request? The one definition. It is the
    // API's own test (`RequestMailService::sendMail`): only an explicit `false` is off, so a
    // missing `emails` or `sendMail` key counts as ON, exactly as the server sends.
    requestMailOff () {
      var a = this.actionData || this.rowData

      return !!(a && a.emails && a.emails.sendMail === false)
    },

    // The tooltip of the Send / Re-send button while Send E-mails is off (a disabled
    // button does not fire mouse events, so the title sits on a wrapping element).
    approvalMailOffTitle () {
      var a = this.actionData || this.rowData

      return activityText('requestMail.step.mailOffTitle', { rtx: a ? a.actionType : null })
    },

    // The label of step 2's and step 5's e-mail button (D-2026-10-16c): "Send {status}
    // e-Mail" until that content has been e-mailed, then "Re-send {status} e-Mail". The
    // server decides which: `approvalMail` is on the step row only while the e-mailed
    // content is unchanged. While one is being sent the button reads "Sending…".
    //
    // XOL-67: for an RTA's decision e-mail (`event` 'rta_decision') `step` is the Tower Co decision,
    // `state.rta.tc`, and the same rule reads its `decisionMail`.
    approvalSendLabel (step, event) {
      if (this.approvalMailSending) {
        return activityText('requestMail.send.sending')
      }

      return sendLabel(step && step.approvedStatus ? step.approvedStatus : 'Pending Approval', !!mailOfDecision(step, event))
    },

    // The click of step 2's / step 5's Send / Re-send {status} e-Mail. The changed fields are
    // saved first (the ordering rule, XOL-582: what the recipients read is what the app
    // holds; nothing goes on if one of those saves failed, and the burst's own notice says
    // so). Then:
    //   - the FIRST Send is one click: the component's `sendApprovalMail()` asks the API to
    //     send to everyone;
    //   - a RE-SEND (`step.approvalMail` is set) opens the recipient picker (Delta 1, item
    //     4) instead of sending: the user chooses who gets it, and `submitResend` sends.
    // A second click while one runs does nothing. Resolves the notify result, or null when
    // nothing was sent (a Re-send resolves null: the picker is what happens next).
    //
    // XOL-67: the same flow serves an RTA's decision e-mail, which has no step. `opts`:
    //   event  'approval_notice' (the default: `step` is a step row) or 'rta_decision' (`step` is
    //          the decision, `state.rta.tc`; its Re-send test is `decisionMail`)
    //   flush  function () -> Promise<Boolean>: what to save first. The default is the step
    //          rows' flush (flushBeforeSend); an RTA has no step rows and passes its own
    //   send   function () -> Promise: the first Send. The default for a step is the
    //          component's sendApprovalMail(); for another event, notifyRequest(event, {})
    sendApprovalEmail (step, opts) {
      var self = this

      if (this.approvalMailSending || this.resendPicker.visible) {
        return Promise.resolve(null)
      }

      opts = opts || {}

      var event = opts.event || 'approval_notice'
      var resend = !!mailOfDecision(step, event)
      var opener = typeof document !== 'undefined' ? document.activeElement : null

      this.approvalMailSending = true

      var release = function () {
        self.approvalMailSending = false
      }

      var flush = typeof opts.flush === 'function' ? opts.flush : function () {
        return self.flushBeforeSend([step, self.actionData])
      }

      return flush().then(function (ok) {
        if (!ok) {
          return null
        }

        if (resend) {
          // The dialog opens at once, in its loading state; the button is free again.
          release()
          self.openResendPicker(event === 'approval_notice' ? step : { event: event, stepId: null, status: step.approvedStatus || null, hadMail: true }, opener)
          return null
        }

        if (typeof opts.send === 'function') {
          return opts.send()
        }

        return event === 'approval_notice' ? self.sendApprovalMail() : self.notifyRequest(event, {})
      }).then(function (result) {
        release()
        return result
      }, function (e) {
        console.error('XOL-586: sending an approval e-mail threw', e)
        release()
        return null
      })
    },

    // ---- The Re-send recipient picker (Delta 1, item 4) ------------------------------

    // Open the picker on this step and ask the API who the e-mail would go to now.
    // `step` is a step row, or (XOL-67) a target { event: 'rta_decision', stepId: null, status,
    // hadMail } for an e-mail that has no step.
    openResendPicker (step, opener) {
      var picker = this.resendPicker

      picker.visible = true
      picker.loading = true
      picker.preview = null
      picker.error = null
      picker.sending = false
      picker.stale = null

      if (step.event) {
        picker.event = step.event
        picker.stepId = step.stepId || null
        picker.status = step.status || null
        picker.hadMail = step.hadMail !== false
      } else {
        picker.event = 'approval_notice'
        picker.stepId = step.id
        picker.status = step.approvedStatus || null
        picker.hadMail = !!step.approvalMail
      }

      picker.opener = opener || null

      return this.loadResendPreview()
    },

    // GET /actions/{id}/notify/preview?event=approval_notice&stepId= (an RTA's decision e-mail:
    // ?event=rta_decision, no stepId). READ-ONLY: it writes
    // nothing. The picker keeps the list it has while this runs (a refresh after a stale
    // refusal), and an answer that arrives after the picker was closed or re-opened is dropped.
    loadResendPreview () {
      var self = this
      var picker = this.resendPicker
      var action = this.actionData || this.rowData
      var seq = ++this.resendSeq
      var event = picker.event || 'approval_notice'

      // A step's e-mail is asked for by step; a decision e-mail has none.
      if (!action || !action.id || (event === 'approval_notice' && !picker.stepId)) {
        return Promise.resolve(null)
      }

      picker.loading = true
      picker.error = null

      // The query goes into the path BEFORE getEndpoint, which appends the caller's orgId /
      // orgCategory to whatever it is given (with '&' when the path already has a '?').
      var url = this.getEndpoint('/actions/' + action.id + '/notify/preview?event=' + event + (picker.stepId ? '&stepId=' + encodeURIComponent(picker.stepId) : ''))

      return axios.get(url).then(function (response) {
        if (seq !== self.resendSeq || !picker.visible) {
          return null
        }

        var body = response ? response.data : null

        if (!body || !Array.isArray(body.recipients) || typeof body.version !== 'string') {
          picker.loading = false
          picker.preview = null
          picker.error = 'generic'
          return null
        }

        // The SPA never asks while Send E-mails is off; if the server says it is (another user
        // turned it off since this tab loaded), say nothing and re-read the request, so the
        // banner appears and the button disables (item 1).
        if (body.sendMail === false) {
          self.closeResendPicker()
          return self.refetchActionById(action.id).then(function () { return null }, function () { return null })
        }

        picker.loading = false
        picker.preview = body
        return body
      }, function (err) {
        if (seq !== self.resendSeq || !picker.visible) {
          return null
        }

        var response = err && err.response
        var status = response ? response.status : null
        var data = (response && response.data && typeof response.data === 'object') ? response.data : {}
        var code = typeof data.code === 'string' ? data.code : null

        console.error('XOL-586: could not load the Re-send recipients', status, err)

        // The same refusals a send gets: the dialog does not open, the refusal's toast says why.
        if (status === 409 && (code === 'request_closed' || code === 'invalid_state')) {
          var result = { ok: false, status: status, code: code, data: data }

          self.closeResendPicker()
          self.showMailToast(notifyOutcome(event, self.notifyContext(action, picker.stepId ? { stepId: picker.stepId } : {}, event), result))
          return null
        }

        picker.loading = false
        picker.preview = null
        picker.stale = null
        picker.error = status === 403 ? 'forbidden' : (status === 404 ? 'notFound' : 'generic')
        return null
      })
    },

    // "Try again" on the picker's error state.
    retryResendPreview () {
      return this.loadResendPreview()
    },

    // Cancel, the ×, Esc. Closing while a send is running does not cancel it: its toast and
    // the re-read of the request still happen.
    closeResendPicker () {
      this.resendSeq++
      this.resendPicker.visible = false
      this.resendPicker.loading = false
    },

    // The dialog's Send: POST .../notify with the ticked addresses and the `version` of the
    // list the user saw. ONE fresh idempotencyKey per click (notifyRequest makes it). The
    // server only intersects the selection with the live recipients, so an address it no
    // longer has comes back as 409 preview_stale: nothing was sent, and the list is read again.
    submitResend (keys) {
      var self = this
      var picker = this.resendPicker

      if (picker.sending || picker.loading || !picker.preview || !Array.isArray(keys) || keys.length === 0) {
        return Promise.resolve(null)
      }

      var seq = this.resendSeq
      var version = picker.preview.version

      picker.sending = true

      var event = picker.event || 'approval_notice'
      var extra = { recipients: keys, previewVersion: version }

      // A step's e-mail names its step; an RTA's decision e-mail has none.
      if (event === 'approval_notice' || picker.stepId) {
        extra.stepId = picker.stepId
      }

      return this.notifyRequest(event, extra).then(function (result) {
        picker.sending = false

        var status = result.status
        var code = result.code
        var data = result.data || {}
        var open = picker.visible && seq === self.resendSeq

        if (status === 409 && code === 'preview_stale') {
          if (open) {
            picker.stale = { reason: data.reason || null, unknown: Array.isArray(data.unknown) ? data.unknown : [] }
            return self.loadResendPreview().then(function () { return result })
          }
          return result
        }

        // Nothing to say and nothing sent; the dialog's Send is disabled at 0 selected.
        if (status === 422 && code === 'empty_selection') {
          return result
        }

        // A network failure, a 429 and a 5xx (after axios-retry): the toast says so, the
        // selection stays, and the user may try again.
        if (status === null || status === undefined || status === 429 || status >= 500) {
          return result
        }

        // Sent (200), or refused in a way that retrying will not fix (a committed 422, 409
        // request_closed / invalid_state, 403, mail_off): done. notifyRequest has re-read the
        // request when a batch was left behind.
        if (open) {
          self.closeResendPicker()
        }

        return result
      }, function (e) {
        console.error('XOL-586: the Re-send threw', e)
        picker.sending = false
        return null
      })
    },

    // The props of <resend-recipients-dialog> (steps 2 and 5 share them).
    resendDialogProps () {
      var picker = this.resendPicker
      var action = this.actionData || this.rowData
      // An RTA's client is `client` (the request types' is `organisation`).
      var org = action && (action.organisation || action.client) ? (action.organisation || action.client) : null

      return {
        visible: picker.visible,
        loading: picker.loading,
        preview: picker.preview,
        error: picker.error,
        sending: picker.sending,
        stale: picker.stale,
        status: picker.status,
        hadMail: picker.hadMail,
        opener: picker.opener,
        owner: org ? (org.name || org.shortName || null) : null,
        towerCo: this.towerCoName(),
        rtx: action ? action.actionType : null
      }
    },

    // What a notify toast names: the request type, the step (an approval e-mail's
    // status and number) or the party's organisation (a test).
    notifyContext (action, extra, event) {
      var ctx = { rtx: action.actionType, towerCo: this.towerCoName() }
      var party = extra && extra.party
      var org = action.organisation

      if (party === 'towerco') {
        ctx.org = this.towerCoName()
      } else if (party === 'owner') {
        ctx.org = org ? (org.shortName || org.name) : null
      } else if (party === 'provider') {
        ctx.org = this.providerLabel()
      }

      ctx.party = party || null

      if (extra && extra.stepId && Array.isArray(action.steps)) {
        for (var i = 0; i < action.steps.length; i++) {
          if (Number(action.steps[i].id) === Number(extra.stepId)) {
            ctx.stepNo = action.steps[i].stepNo
            ctx.status = action.steps[i].approvedStatus || 'Pending Approval'
            ctx.repeat = !!action.steps[i].approvalMail
          }
        }
      }

      // XOL-67: the decision e-mail of an RTA has no step: its status and its Re-send test are the
      // Tower Co decision's, and it is step 4 of the five.
      if (event === 'rta_decision') {
        var tc = action.state && action.state.rta && action.state.rta.tc

        ctx.stepNo = 4
        ctx.status = tc && tc.approvedStatus ? tc.approvedStatus : null
        ctx.repeat = !!(tc && tc.decisionMail)
      }

      return ctx
    },

    // A notify result: a plain `$message`, or a `$notify` with one line per sentence.
    showMailToast (toast) {
      showToast(this, toast)
    },

    // The command runner (B4, R2.12). A workflow command is NOT a content save:
    // approval, complete, reopen, reject and complete-request go through here and
    // nowhere else (the legacy whole-row writes are gone).
    //
    //   cmd = {
    //     rows:         [step row, action row ...]  the rows the command touches
    //     url:          '/step/12/approval'         endpoint, relative
    //     body:         {...} or function () -> {...}  defaults to {}; a function is
    //                   evaluated when the POST is sent (after the flush)
    //     label:        'Approval'                  for send()'s item name
    //     requireSaved: true for reject / complete-request: do NOT send when a
    //                   pending content save of those rows failed (the ordering rule)
    //     ordering:     { kind: 'reject' | 'complete', rtx }  the footer of that
    //                   failure's notice ("The request wasn't closed because ...")
    //     actionId:     for the re-GET after a network failure
    //     verify:       function (freshAction) -> Boolean: is the outcome there?
    //     onBusy:       function (Boolean)          extra busy listener
    //     hold:         { row, keys }               owned keys / paths the command is
    //                   deciding: an adopt meanwhile leaves their local value alone
    //                   (the control keeps the user's choice and its indicator)
    //     onLate:       function (outcome)          the outcome that arrives AFTER the
    //                   user stopped waiting (status 'unknown')
    //   }
    //
    // 1. the form pause is cut short and the rows' changed fields are flushed; a
    //    flush that hangs asks the user (Keep waiting / Cancel, 15 s);
    // 2. the command waits for, and then holds, the rows' queues;
    // 3. 2xx (a replay is a 200 too): adopt {step, action}; 409 (stale_decision,
    //    invalid_state, request_closed): adopt the body, the server wins; 403 /
    //    422 / 404: refused; a network/5xx failure: re-GET and ask `verify`.
    // 4. the POST's own wait is under the same hang dialog (axiosClient has no
    //    timeout): after 15 s, Keep waiting / Stop waiting. Stopping resolves
    //    { status: 'unknown' } at once (busy released) and a late answer is still
    //    adopted, and handed to `onLate`.
    //
    // The command is "busy" for its WHOLE run (flush wait included): the drawer
    // can't close mid-command and Finish stays disabled (UF2).
    //
    // Resolves { ok, status, code?, data? }; never rejects. status: ok,
    // recovered, conflict, closed, forbidden, invalid, gone, refused,
    // not-applied, content-not-saved, cancelled, unknown, blocked, version-required,
    // failed.
    runCommand (cmd) {
      var self = this
      var rows = cmd.rows || []
      var keys = []

      for (var i = 0; i < rows.length; i++) {
        keys.push(sync.recordFor(rows[i]).queueKey)
      }

      var busy = function (on) {
        self.reportCommandBusy(on)

        if (typeof cmd.onBusy === 'function') {
          cmd.onBusy(on)
        }
      }

      // The hold on the keys the command decides, released exactly once: before
      // the response is adopted (so the response decides), or when the run ends.
      var held = false
      var releaseHold = function () {
        if (held) {
          held = false
          sync.releaseOwned(cmd.hold.row, cmd.hold.keys)
        }
      }
      var run = extend({}, cmd)

      run.releaseHold = releaseHold

      busy(true)

      if (cmd.hold) {
        held = sync.holdOwned(cmd.hold.row, cmd.hold.keys)
      }

      var flushing = sync.flushRows(rows, { reason: 'command', ordering: cmd.requireSaved ? cmd.ordering : null })

      return waitForSaves(this.$root, flushing, { leaveLabel: copyText('requestSync.hang.cancel') }).then(function (waited) {
        // "Cancel" while the saves hung: the command is not sent.
        if (waited.left) {
          return { ok: false, status: 'cancelled' }
        }

        var flushed = waited.result

        if (cmd.requireSaved && !(flushed && flushed.ok)) {
          return { ok: false, status: 'content-not-saved', flush: flushed }
        }

        var sending = sync.enqueueAll(keys, function () {
          return self.sendCommand(run)
        })

        return waitForSaves(self.$root, sending, {
          leaveLabel: copyText('requestSync.hang.stopWaiting'),
          noListText: copyText('requestSync.hang.commandUnknown')
        }).then(function (sent) {
          if (sent.left) {
            // The user stopped waiting: busy is released, the outcome is unknown,
            // and the answer, when it comes, is adopted by sendCommand as usual.
            sending.then(function (late) {
              try {
                if (typeof cmd.onLate === 'function') {
                  cmd.onLate(late)
                }
              } catch (e) {
                console.error('XOL-596: handling a late command outcome threw', cmd.url, e)
              }
            })

            return { ok: false, status: 'unknown' }
          }

          return sent.result
        })
      }).then(function (out) {
        releaseHold()
        busy(false)

        return out || { ok: false, status: 'failed' }
      }, function (e) {
        console.error('XOL-596: running a command threw', cmd.url, e)
        releaseHold()
        busy(false)

        return { ok: false, status: 'failed', err: e }
      })
    },

    // Count a running command on this component and on the drawer
    // (RequestWorkflow provides `requestCommandHost`; the workflow itself has
    // `commandBusy`). `cmdRunning` only makes this component refuse a second lock or
    // unlock while one runs; no control is bound to it (the approval controls are
    // fields and stay enabled).
    reportCommandBusy (on) {
      this.cmdRunning = Math.max(0, (this.cmdRunning || 0) + (on ? 1 : -1))

      if (this.requestCommandHost && typeof this.requestCommandHost.busy === 'function') {
        this.requestCommandHost.busy(on)
      } else if (typeof this.commandBusy === 'function') {
        this.commandBusy(on)
      }
    },

    sendCommand (cmd) {
      var self = this
      var seq = sync.nextSeq()
      var rows = cmd.rows || []

      // THE WORKFLOW FLOOR (see requestRowSync): from now until it is answered the
      // command is in flight on its rows, and every other adopt leaves their
      // workflow-owned keys alone. Answered exactly once, BEFORE anything is
      // adopted or re-read, so the re-GET after a network failure is not blocked.
      sync.commandSent(rows)

      var answered = false
      var answer = function () {
        if (!answered) {
          answered = true
          sync.commandAnswered(rows)
        }
      }

      return new Promise(function (resolve) {
        var settled = false
        var finish = function (outcome) {
          answer()

          if (!settled) {
            settled = true
            resolve(outcome)
          }
        }

        var called = false

        // Quiet: send() raises no toast of its own; the command's handler shows
        // SPA copy built from the adopted rows (UF1).
        // `body` may be a function: evaluated just before the POST, after the flush, so
        // an `expected...` decision is the one the server holds once the user's own
        // pending decision has been saved.
        self.send(self, self.getEndpoint(cmd.url), (typeof cmd.body === 'function' ? cmd.body() : cmd.body) || {}, cmd.label || 'Request',
          function (data) {
            called = true
            answer()
            // The response decides what the control shows: let go of the hold first.
            if (cmd.releaseHold) { cmd.releaseHold() }
            // `command`: its owned keys are set whatever its seq.
            self.adoptSaveResponse(data, { seq: seq, command: true })
            finish({ ok: true, status: 'ok', data: data })
          },
          function (err) {
            called = true
            answer()
            if (cmd.releaseHold) { cmd.releaseHold() }
            self.commandFailed(cmd, err, seq).then(finish, function () {
              finish({ ok: false, status: 'failed' })
            })
          },
          axios.post, false, 'Save', null, true, false, true
        ).then(function () {
          if (!called) {
            finish({ ok: false, status: 'failed' })
          }
        }, function (e) {
          // As in sendFieldSave: a throwing failure handler rejects send().
          console.error('XOL-596: handling a command failure threw', cmd.url, e)
          finish({ ok: false, status: 'failed', err: e })
        })
      })
    },

    commandFailed (cmd, err, seq) {
      var self = this

      // send()'s view-only guard calls failCallback with nothing: not sent.
      if (err === undefined || err === null) {
        return Promise.resolve({ ok: false, status: 'blocked' })
      }

      var response = err.response
      var data = (response && response.data && typeof response.data === 'object') ? response.data : {}
      var status = response ? response.status : null

      if (!response || status >= 500) {
        // The final attempt failed after axios-retry: whatever the server holds
        // now is the truth. Re-GET, adopt, and ask whether the outcome is there.
        return this.refetchActionById(cmd.actionId).then(function (fresh) {
          if (fresh && typeof cmd.verify === 'function' && cmd.verify(fresh)) {
            return { ok: true, status: 'recovered', data: fresh }
          }

          return { ok: false, status: 'not-applied', err: err }
        })
      }

      // 428: send() already raised the reload prompt (commands take no
      // contentVersion, so this is a safety net only).
      if (status === 428) {
        return Promise.resolve({ ok: false, status: 'version-required', code: data.code || null, err: err })
      }

      if (status === 409) {
        // The 409 body is the server's answer to this command: its owned keys win.
        self.adoptSaveResponse(data, { seq: seq, command: true })

        return Promise.resolve({
          ok: false,
          status: data.code === 'request_closed' ? 'closed' : 'conflict',
          code: data.code || null,
          data: data
        })
      }

      if (status === 403) {
        return this.refetchAfterRefusal(cmd, data).then(function () {
          return { ok: false, status: 'forbidden', code: data.code || null, err: err }
        })
      }

      // 422 (XOL-595 A2: a malformed body is refused BEFORE the closed check): a
      // bug signal.
      if (status === 422) {
        console.error('XOL-596: command refused as invalid', cmd.url, data.code, data.fields)
        return this.refetchAfterRefusal(cmd, data).then(function () {
          return { ok: false, status: 'invalid', code: data.code || null, fields: data.fields, err: err }
        })
      }

      // 404 not_found (A1): the step or action is missing. Re-GET: when the
      // action itself is gone the caller closes the drawer with an error; when
      // it is still there the command was simply refused.
      if (status === 404) {
        return this.refetchActionResult(cmd.actionId).then(function (res) {
          return res.gone
            ? { ok: false, status: 'gone', code: data.code || 'not_found', err: err }
            : { ok: false, status: 'refused', code: data.code || 'not_found', err: err }
        })
      }

      // 500 server_error is the `>= 500` branch above: the command may or may
      // not have been applied, so it asks the server (`verify`).
      return Promise.resolve({ ok: false, status: 'failed', code: data.code || null, err: err })
    },

    // A 403 or 422 refusal of a command tells the screen nothing about the rows: the
    // control goes back to its baseline, but the baseline itself may be what was
    // stale (the decision was made elsewhere, or the lock was taken since the
    // drawer opened). When the refusal carries rows they are adopted; when it does
    // not, ONE re-GET of the action is. Its seq is taken now, after the answer, so
    // the workflow floor lets it through and the screen shows the truth.
    refetchAfterRefusal (cmd, data) {
      if (data && (data.action || data.step)) {
        // A seq taken now, after the answer (like the re-GET below), so the floor
        // lets the rows' workflow-owned keys through.
        this.adoptSaveResponse(data, { seq: sync.nextSeq() })

        return Promise.resolve(true)
      }

      return this.refetchActionById(cmd.actionId).then(function () {
        return true
      })
    },

    // A workflow command with every outcome handled (UX items 5, 6, 10, 11, 12).
    //
    //   spec = {
    //     kind:      'prgApproval' | 'lock' | 'unlock' | 'complete' | 'reopen' | 'reject' | 'completeRequest'
    //     step:      the row the command acts on (what the notices read)
    //     rows, url, body, label, requireSaved, ordering, verify   (runCommand's)
    //     choice:    what the user asked for ('Approved'), for the stale copy
    //     step2p1, lastStep: rows some notices name
    //     revert:    function (): put the control(s) back to the baseline
    //     onOk:      function (outcome): the success side effects
    //   }
    //
    // The decision lives on the SERVER: nothing is stamped locally (no
    // approved_by / completed_at / currentStep / status writes); the local row
    // changes only through the adopt of the response. A failure puts the control
    // back (XOL-40: never show progress the server didn't accept). Resolves the
    // runCommand outcome.
    workflowCommand (spec) {
      var self = this
      var action = this.actionData || this.rowData

      return this.runCommand({
        rows: spec.rows,
        url: spec.url,
        body: spec.body,
        label: spec.label,
        requireSaved: spec.requireSaved,
        ordering: spec.ordering,
        actionId: action ? action.id : null,
        verify: spec.verify,
        hold: spec.hold,
        // The answer that comes after the user stopped waiting: a success shows its
        // normal message; anything else its normal notice. (The control was put
        // back when they stopped.)
        onLate: function (late) {
          self.commandSettled(spec, late)
        }
      }).then(function (out) {
        self.commandSettled(spec, out)

        return out
      })
    },

    // The success side effects or the refusal notice of a settled command. Never
    // throws: a throwing `onOk` or notice must not leave a confirm or a control
    // locked, and it cannot be an unhandled rejection.
    commandSettled (spec, out) {
      // A quiet command (a drawer close locking several approvals) raises nothing of
      // its own: its caller reads the outcome and speaks once for all of them.
      if (spec.quiet) {
        return
      }

      try {
        if (out.ok) {
          if (typeof spec.onOk === 'function') {
            spec.onOk(out)
          }
        } else {
          this.commandRefused(spec, out)
        }
      } catch (e) {
        console.error('XOL-596: handling a command outcome threw', spec.url, e)
      }
    },

    // The notice and the revert of a command that did not go through.
    commandRefused (spec, out) {
      var action = this.actionData || this.rowData
      var ctx = {
        root: this.$root,
        step: spec.step,
        action: action,
        choice: spec.choice,
        rtx: action ? action.actionType : null,
        step2p1: spec.step2p1,
        lastStep: spec.lastStep
      }
      var revert = function () {
        if (typeof spec.revert === 'function') {
          spec.revert()
        }
      }

      // 403 approval_locked: someone locked this approval since the drawer opened.
      // The refusal's rows are adopted (they say who and when); the control goes
      // back to the locked decision.
      if (out.status === 'forbidden' && out.code === 'approval_locked' &&
        spec.kind === 'prgApproval') {
        revert()
        savePopup.notify(commandNotices.lockedNotice(spec.kind, extend({ canUnlock: this.canUnlockApproval(spec.step) }, ctx)))
        return
      }

      // 403 step_completed on Lock / Unlock (XOL-596): a stale screen showed the buttons
      // of a step someone completed meanwhile. The refusal's rows are adopted, so the
      // step is frozen on screen now; the section names who completed it.
      if (out.status === 'forbidden' && out.code === 'step_completed' && (spec.kind === 'lock' || spec.kind === 'unlock')) {
        revert()
        savePopup.notify(commandNotices.stepCompletedNotice(this.$root, {
          n: spec.step.stepNo,
          what: spec.kind,
          kind: spec.kind,
          p1: spec.step
        }))
        this.focusFrozenHint()
        return
      }

      switch (out.status) {
        case 'conflict':
          // The adopt already put the server's rows on screen: nothing to revert.
          savePopup.notify(commandNotices.conflictNotice(spec.kind, out, ctx))
          break

        case 'closed':
          savePopup.notify(commandNotices.closedNotice(ctx))
          break

        case 'gone':
          savePopup.notify(commandNotices.goneNotice())
          revert()

          if (this.requestCommandHost && typeof this.requestCommandHost.close === 'function') {
            this.requestCommandHost.close()
          } else if (typeof this.closeRequestDrawer === 'function') {
            this.closeRequestDrawer()
          }
          break

        case 'invalid':
          // A bug signal: commandFailed already re-read and adopted the action, so
          // the baseline the control goes back to is the server's. Say so.
          revert()
          savePopup.notify(commandNotices.readOnlyNotice(out))
          break

        case 'unknown':
          // The user stopped waiting: back to what the server last said, and an
          // honest notice that the outcome is not known yet.
          revert()
          savePopup.notify(commandNotices.unknownNotice())
          break

        case 'content-not-saved':
        case 'cancelled':
        case 'blocked':
        case 'version-required':
          // The save's own notice (or the user's Cancel, or the 428 prompt) said
          // it; the control just goes back.
          revert()
          break

        default:
          // forbidden, refused (404 with the request still there), not-applied
          // (the re-GET lacks the outcome), failed.
          revert()
          savePopup.notify(commandNotices.notSavedNotice(spec.kind, ctx))
      }
    },

    // Put workflow-owned columns of a row back to what the server last said (the
    // baseline), after a command failed. `state.step3.legalApproved` is
    // written in place.
    revertWorkflowFields (row, keys) {
      var base = sync.getBaseline(row)

      for (var i = 0; i < keys.length; i++) {
        if (base[keys[i]] === undefined) {
          continue
        }

        if (keys[i].indexOf('.') !== -1) {
          sync.assignPath(row, keys[i], base[keys[i]])
        } else {
          Vue.set(row, keys[i], base[keys[i]])
        }
      }
    },

    // The server's row for a step id inside a re-GET'd action (for `verify`).
    stepOfAction (fresh, stepId) {
      var steps = fresh && fresh.steps ? fresh.steps : []

      for (var i = 0; i < steps.length; i++) {
        if (Number(steps[i].id) === Number(stepId)) {
          return steps[i]
        }
      }

      return null
    },

    /* ====================================================================
       XOL-596 DECISION FIELDS (Carlos 2026-10-14): "Make this section work the same
       way as other fields do."

       The approval controls (step 2 status and date, step 3 legal tick, step 5
       status and date) are FIELDS. A change marks the field changed and restarts the
       form's 3 s pause (`decisionChanged`); at the pause, or any flush point, the
       group's latest values go out ONCE as one approval command (`sendDecisionSave`,
       registered as each row's `decisionSender`). The server's approval command is
       the transport, so workflow state stays server-owned. The group machinery lives
       in lib/requestRowSync (registerDecision, G-DECIDE).
       ==================================================================== */

    // May the viewer change this step's decision? (The role gate; a lock is the
    // server's to refuse, 403 approval_locked.)
    canDecideApproval (step) {
      return approvalLock.canDecide(this, this.actionData || this.rowData, step)
    },

    // Register the decision groups of an action's party-1 steps 2, 3 and 5. Called once
    // by the drawer, after its rows are registered.
    syncRegisterDecisions (action) {
      var steps = action && action.steps ? action.steps : []

      for (var i = 0; i < steps.length; i++) {
        this.syncDecisionGroup(steps[i], action)
      }
    },

    // The group for a party-1 step row: steps 2 and 5 live on the step row; step 3's
    // tick lives on the ACTION row. Returns the record that holds the v-model.
    syncDecisionGroup (step, action) {
      var kind = approvalLock.approvalStepKind(action, step)
      var rec

      if (kind === 'step2' || kind === 'step5') {
        this.syncEnsure(step, 'step')
        rec = sync.registerDecision(step, {
          kind: kind,
          paths: ['approvedStatus', 'approved_at'],
          commandRow: step,
          rows: kind === 'step5' ? [step, action] : [step]
        })
      } else if (kind === 'step3') {
        this.syncEnsure(action, 'action')
        this.syncEnsure(step, 'step')
        rec = sync.registerDecision(action, {
          kind: kind,
          paths: [LEGAL_PATH],
          commandRow: step,
          rows: [step, action]
        })
      }

      return rec || null
    },

    // A step component mounted without the drawer (or before it registered): make sure
    // the row it edits has its group and a sender, so nothing is silently dropped.
    // `row` is a party-1 step row (steps 2 and 5) or the action row (step 3).
    syncEnsureDecision (row) {
      var action = this.actionData || this.rowData

      if (!row) {
        return null
      }

      if (row === action) {
        var steps = action.steps || []

        for (var i = 0; i < steps.length; i++) {
          if (approvalLock.approvalStepKind(action, steps[i]) === 'step3') {
            return this.ensureDecisionOn(action, steps[i], action)
          }
        }

        return null
      }

      return this.ensureDecisionOn(row, row, action)
    },

    ensureDecisionOn (holder, step, action) {
      var rec = sync.getRecord(holder)

      if (!rec || !rec.decision || !rec.decisionSender) {
        this.syncEnsure(holder, holder === action ? 'action' : 'step')
        rec = this.syncDecisionGroup(step, action)
      }

      return rec
    },

    // The change handler of a decision control, called AFTER the v-model changed. A
    // viewer who may not decide gets the displayed value put back and nothing is
    // recorded. Otherwise: the field is changed and the pause restarts, nothing more.
    decisionChanged (row, path) {
      var rec = this.syncEnsureDecision(row)

      if (!rec || !rec.decision) {
        return false
      }

      // A LOCKED approval is read-only (its controls are disabled): a change that
      // reaches here anyway is put back. A lock that arrives AFTER the change was
      // recorded is the server's to refuse (403 approval_locked), at the save.
      // A COMPLETED step is read-only too (XOL-596): the same put-back. A completion
      // that arrives after the change was recorded is the server's to refuse, 403
      // step_completed, at the save.
      if (!this.canDecideApproval(rec.decision.commandRow) || this.approvalLocked(rec.decision.commandRow) ||
        this.isStepFrozen(rec.decision.commandRow.stepNo)) {
        this.putBackDecision(rec, rec.decision.paths)
        return false
      }

      return sync.fieldChanged(row, path)
    },

    // Put decision paths back to the last word the server gave (the baseline).
    putBackDecision (rec, paths) {
      for (var i = 0; i < paths.length; i++) {
        if (rec.workflow[paths[i]] !== undefined || paths[i] === LEGAL_PATH) {
          sync.assignPath(rec.row, paths[i], sync.decisionBase(rec, paths[i], paths[i]))
        }
      }
    },

    // The date picker's clear (×): the field takes the date it had (its base), nothing
    // is recorded, no message. A pending status pick in the same pause is KEPT.
    decisionDateCleared (row) {
      var rec = sync.getRecord(row)

      if (!rec || !rec.decision) {
        return
      }

      var entry = rec.entries.approved_at

      sync.assignPath(rec.row, 'approved_at', entry ? sync.cloneValue(entry.value) : sync.decisionBase(rec, 'approved_at', 'approved_at'))
    },

    // The request body of one decision group: from the values and BASES of the
    // request, built when the save runs (G-DECIDE: `expected...` is the base).
    decisionBody (req) {
      var v = req.values
      var b = req.bases

      if (req.decision.kind === 'step3') {
        return { approvedStatus: v[LEGAL_PATH] ? 'Approved' : 'Pending', expectedApproved: !!b[LEGAL_PATH] }
      }

      var status = v.approvedStatus === null || v.approvedStatus === undefined || v.approvedStatus === '' ? 'Pending' : v.approvedStatus
      var body = {
        approvedStatus: status,
        expectedApprovedStatus: b.approvedStatus === null || b.approvedStatus === undefined || b.approvedStatus === '' ? null : b.approvedStatus
      }

      // The date goes only when the user changed it. A status-only change lets the
      // server stamp today; Pending clears the date.
      if (req.changed.indexOf('approved_at') !== -1 && v.approved_at && status !== 'Pending') {
        body.approved_at = sync.decisionDay(v.approved_at)
      }

      return body
    },

    // The `sender` of a decision group: one approval command. Runs inside the rows'
    // queue (after the content saves of its pause), so it neither flushes nor queues
    // itself and never reports "busy": a decision save is a field save, and the C-12
    // card carries Pending, Saving and Saved. Resolves ONE outcome for the group;
    // requestRowSync fans it out per changed path.
    sendDecisionSave (req) {
      var self = this
      var rec = req.rec
      var spec = req.decision
      var step = spec.commandRow
      var action = this.actionData || this.rowData
      var done = function (status, extra) {
        return extend({ status: status, decisionStepId: step.id }, extra)
      }

      // Defence in depth, like G-OWN: the SPA's gate is the only role guard.
      if (!this.canDecideApproval(step)) {
        return Promise.resolve(done('blocked'))
      }

      // After a 428 the page is out of date: nothing is sent, edits stay on screen.
      if (sync.syncState.versionRequiredSeen) {
        return Promise.resolve(done('version-required'))
      }

      var body = this.decisionBody(req)
      var requested = body.approvedStatus
      var verify = function (fresh) {
        var row = self.stepOfAction(fresh, step.id)

        if (!row) {
          return false
        }

        if (spec.kind === 'step3') {
          return !!row.approved === (requested === 'Approved')
        }

        return (row.approvedStatus || 'Pending') === requested && (!body.approved_at || String(row.approved_at).slice(0, 10) === body.approved_at)
      }
      var cmd = {
        rows: spec.rows,
        url: '/step/' + step.id + '/approval',
        body: body,
        label: spec.kind === 'step3' ? 'Legal approval' : 'Approval',
        actionId: action ? action.id : null,
        verify: verify
      }

      var finish = function (out) {
        return self.decisionOutcome(req, out, done)
      }

      if (req.keepalive) {
        return this.sendDecisionKeepalive(req, cmd).then(finish)
      }

      return this.sendCommand(cmd).then(finish, function (e) {
        console.error('XOL-596: sending a decision threw', rec.queueKey, e)

        return done('failed', { err: e })
      })
    },

    // The page is being left: the same body, sent with fetch keepalive. Its answer, if
    // the page stays, is handled exactly like an axios one.
    sendDecisionKeepalive (req, cmd) {
      var self = this
      var rows = cmd.rows
      var url = this.getEndpoint(cmd.url)
      var answered = false
      var answer = function () {
        if (!answered) {
          answered = true
          sync.commandAnswered(rows)
        }
      }
      var seq = sync.nextSeq()

      sync.commandSent(rows)

      return keepalivePost(url, cmd.body).then(function (res) {
        answer()
        self.adoptSaveResponse(res.data, { seq: seq, command: true })

        return { ok: true, status: 'ok', data: res.data }
      }, function (err) {
        answer()

        if (err && err.response && err.response.status === 428) {
          sync.promptReload(self, { url: url })
        }

        return self.commandFailed(cmd, err, seq)
      }).catch(function (e) {
        console.error('XOL-596: handling a keepalive decision threw', url, e)
        answer()

        return { ok: false, status: 'failed', err: e }
      })
    },

    // The server's value of a decision path after the answer was adopted.
    serverDecisionValue (rec, path) {
      return rec.deferred[path] ? sync.cloneValue(rec.deferred[path].value) : sync.decisionBase(rec, path, path)
    },

    // Map a command outcome to ONE field outcome (D5). A refusal takes the server's
    // value for every path of the group and stashes what the user had, so the card can
    // show it; a network failure leaves the values on screen, still changed.
    decisionOutcome (req, out, done) {
      var self = this
      var rec = req.rec
      var spec = req.decision
      var step = spec.commandRow
      var action = this.actionData || this.rowData
      var code = out.code || null
      var raw = out.data || (out.err && out.err.response ? out.err.response.data : null)
      var data = raw && typeof raw === 'object' ? raw : {}
      var latest = {}

      spec.paths.forEach(function (p) {
        latest[p] = sync.cloneValue(sync.getPath(rec.row, p))
      })

      // Take the server's value on every path of the group; stash the user's changed
      // values; returns { values, serverValues, stashIds }.
      var take = function (status, extra) {
        var serverValues = {}
        var stashIds = {}

        spec.paths.forEach(function (p) {
          serverValues[p] = self.serverDecisionValue(rec, p)
        })

        req.changed.forEach(function (p) {
          var item = sync.stashLost({
            rowKind: rec.kind,
            rowId: rec.id,
            actionType: action ? action.actionType : null,
            stepNo: step.stepNo,
            partyNo: step.partyNo,
            path: p,
            yours: latest[p],
            serverValue: serverValues[p],
            reason: code || status,
            by: step.approved_by || null,
            org: null,
            at: step.decided_at || step.approved_at || null,
            burstId: req.burst ? req.burst.id : null,
            meta: sync.rowMeta(rec)
          })

          stashIds[p] = item.stashId
        })

        spec.paths.forEach(function (p) {
          sync.takeServer(rec, p, undefined)
        })

        return done(status, extend({
          values: latest,
          serverValues: serverValues,
          stashIds: stashIds,
          code: code,
          by: step.approved_by || null,
          at: step.decided_at || step.approved_at || null
        }, extra))
      }

      switch (out.status) {
        case 'ok':
        case 'recovered':
          // Noted when the decision is SAVED, not when it is picked: the close asks
          // about decisions made in this drawer.
          this.noteApprovalDecision(step)

          return done('saved', { values: latest })

        case 'conflict':
          return take(code === 'stale_decision' ? 'stale' : 'failed', { message: data.message || data.error })

        case 'closed':
          return take('closed')

        case 'forbidden':
          // 403 approval_locked: the refusal's rows are adopted (who locked it, when).
          // 403 step_completed (XOL-596): likewise, and focus goes to the step's hint.
          if (code === 'step_completed') {
            this.focusFrozenHint()
          } else {
            this.focusLockedLine()
          }

          return take('locked', { message: data.message || data.error, stepNo: data.stepNo })

        case 'invalid':
          return take('read-only', { requestId: this.requestIdOf(out.err), fields: out.fields })

        case 'gone':
          savePopup.notify(commandNotices.goneNotice())

          if (this.requestCommandHost && typeof this.requestCommandHost.close === 'function') {
            this.requestCommandHost.close()
          } else if (typeof this.closeRequestDrawer === 'function') {
            this.closeRequestDrawer()
          }

          // The gone notice has said it: no second section about the same thing.
          return take('blocked')

        case 'refused':
          return take('failed')

        case 'not-applied':
          // Network / 5xx and the re-GET lacks it: the pick stays on screen, changed.
          return done('network', { err: out.err, values: latest })

        case 'version-required':
          return done('version-required', { values: latest })

        case 'blocked':
          return done('blocked')

        default:
          return done('failed', { err: out.err, code: code, values: latest })
      }
    },

    // A decision control that was focused is disabled by a refusal (the approval got
    // locked): focus moves to the lock line's text, the same target a Lock uses.
    focusLockedLine () {
      var self = this

      this.$nextTick(function () {
        var active = typeof document !== 'undefined' ? document.activeElement : null

        if (active && active !== document.body && !active.disabled) {
          return
        }

        var root = self.$el && typeof self.$el.querySelector === 'function' ? self.$el : null
        var el = root ? root.querySelector('.approval-lock-line__text') : null

        if (el && typeof el.focus === 'function') {
          el.focus()
        }
      })
    },

    /* ====================================================================
       XOL-596 approval lock (Carlos 2026-10-13; XOL-595 plan 4.9)

       Nothing locks by itself. A step's approval is locked by the lock commands
       and unlocked by one; the flag is server-owned (`approvalLocked`, `_by`,
       `_at` on the party-1 step row). The rules live in lib/approvalLock.
       ==================================================================== */

    // 'step2' | 'step3' | 'step5' | 'prg' | null: does this step row have an approval?
    approvalKind (step) {
      return approvalLock.approvalStepKind(this.actionData || this.rowData, step)
    },

    // Is this step's approval locked now? (The ROW: an owned key, adopted from every
    // response, so a lock someone else took shows as soon as any response says so.)
    approvalLocked (step) {
      return !!(step && step.approvalLocked)
    },

    // Does the approval's lock line have anything to say (locked, or decided and not
    // locked)? The header layout reserves room for it below lg only when it does.
    approvalLineShown (step) {
      return this.approvalLocked(step) || this.approvalDecided(step)
    },

    // Is there a decision to lock? From the BASELINE (what the server last said),
    // never the v-model. Reads `syncState.tick`, so a render that calls it follows
    // every adopt.
    approvalDecided (step) {
      void sync.syncState.tick

      return approvalLock.isDecided(this.approvalKind(step), sync.getBaseline(step))
    },

    // Who may lock (Q-L5, UF-L11) and who may unlock. See lib/approvalLock.
    canLockApproval (step) {
      return approvalLock.canLock(this, this.actionData || this.rowData, step)
    },

    canUnlockApproval (step) {
      return approvalLock.canUnlock(this, this.actionData || this.rowData, step)
    },

    // Step 5's final approval gate, also what locks it (UF-L11).
    canDecideFinalStep () {
      return approvalLock.canDecideFinalStep(this)
    },

    // The drawer remembers which approvals the user decided in THIS drawer
    // (RequestWorkflow.sessionDecisions): the close asks about those only. An
    // approval command that succeeded notes the row when the server's decision is a
    // decision, and forgets it when it left the row undecided (Pending, the legal
    // tick removed). Refusals, conflicts and reverts note nothing.
    noteApprovalDecision (step) {
      // A step component reaches the drawer through the command host; the drawer
      // itself (it IS the host, and the decision sender) has the methods of its own.
      var host = this.requestCommandHost

      if (!host || typeof host.noteDecision !== 'function' || typeof host.forgetDecision !== 'function') {
        host = typeof this.noteDecision === 'function' && typeof this.forgetDecision === 'function'
          ? { noteDecision: this.noteDecision, forgetDecision: this.forgetDecision }
          : null
      }

      if (!host) {
        return
      }

      if (this.approvalDecided(step)) {
        host.noteDecision(step)
      } else {
        host.forgetDecision(step)
      }
    },

    // POST /step/{id}/lock. `opts.expected` is the decision the user was shown (the
    // close prompt lists it); the API answers 409 stale_decision when the stored one
    // changed since (UF-L2), and nothing is locked. The pending content saves of the
    // rows go FIRST (the lock refuses remarks, Revise TP and Re-survey) and the lock
    // is not sent if one failed. `opts.quiet`: no notice of its own (a closing
    // drawer speaks once for all of its locks). `opts.ordering` is the footer kind of
    // a failed remarks save ('lock' | 'lockOnClose').
    lockCommand (step, opts) {
      var self = this
      var o = opts || {}
      var action = this.actionData || this.rowData
      var kind = this.approvalKind(step)

      return this.workflowCommand({
        kind: 'lock',
        step: step,
        rows: [step, action],
        url: '/step/' + step.id + '/lock',
        // The decision the user was shown, read AFTER the flush (a function): a
        // decision picked just before the Lock is saved first, and the lock expects it.
        body: o.expected || function () { return approvalLock.expectedBody(kind, sync.getBaseline(step)) },
        label: 'Approval lock',
        requireSaved: true,
        ordering: { kind: o.ordering || 'lock' },
        quiet: !!o.quiet,
        verify: function (fresh) {
          var row = self.stepOfAction(fresh, step.id)

          return !!row && !!row.approvalLocked
        },
        revert: function () {},
        onOk: function () {
          var host = self.requestCommandHost

          if (host && typeof host.forgetDecision === 'function') {
            host.forgetDecision(step)
          }

          savePopup.success(commandNotices.lockDoneMessage('lock', { step: step }).message)
        }
      })
    },

    // POST /step/{id}/unlock. Not a decision: it never adds the row to the drawer's
    // session decisions, so closing after an unlock with nothing decided asks nothing.
    unlockCommand (step) {
      var self = this
      var action = this.actionData || this.rowData

      return this.workflowCommand({
        kind: 'unlock',
        step: step,
        rows: [step, action],
        url: '/step/' + step.id + '/unlock',
        body: {},
        label: 'Approval unlock',
        verify: function (fresh) {
          var row = self.stepOfAction(fresh, step.id)

          return !!row && !row.approvalLocked
        },
        revert: function () {},
        onOk: function () {
          savePopup.success(commandNotices.lockDoneMessage('unlock', { step: step }).message)
        }
      })
    },

    // The lock line's busy label ("Locking…" / "Unlocking…").
    lockBusyLabel () {
      return this.lockRunning === 'unlock'
        ? copyText('requestSync.approvalLock.line.unlocking')
        : copyText('requestSync.approvalLock.line.locking')
    },

    // After a lock command: put focus where the user's attention goes. `which` is
    // the control to land on; the line falls back to its text.
    focusLockLine (which) {
      var self = this

      this.$nextTick(function () {
        var line = self.$refs.lockLine

        if (!line) {
          return
        }

        if (which === 'lock') {
          line.focusLock()
        } else if (which === 'unlock') {
          line.focusUnlock()
        } else {
          line.focusText()
        }
      })
    },

    // The Unlock action of the lock line: a confirm (it removes protection), then
    // POST /step/{id}/unlock. Cancel sends nothing and returns focus to Unlock.
    unlockApproval (step) {
      var self = this
      var action = this.actionData || this.rowData
      var kind = this.approvalKind(step)

      // A completed step takes no lock or unlock (Carlos, 2026-10-14): the line does not
      // render the buttons, and this is the handler's own guard.
      if (!this.canUnlockApproval(step) || this.isStepFrozen(step.stepNo) || this.cmdRunning > 0 || this.lockRunning) {
        return Promise.resolve({ ok: false, status: 'blocked' })
      }

      return approvalLock.askUnlock(this.$root, {
        n: step.stepNo,
        title: approvalLock.stepTitle(action, step),
        decision: approvalLock.decisionText(kind, sync.getBaseline(step), action),
        fields: approvalLock.unlockedFields(kind, action),
        alive: function () { return !self._isDestroyed }
      }).then(function (answer) {
        if (answer !== 'unlock') {
          self.focusLockLine('unlock')

          return { ok: false, status: 'cancelled' }
        }

        self.lockRunning = 'unlock'

        return self.unlockCommand(step).then(function (out) {
          self.lockRunning = null
          self.focusLockLine(out.ok ? 'lock' : 'unlock')

          return out
        })
      })
    },

    // The Lock action of the "Not locked" hint: no confirm (locking is encouraged and
    // an Unlock undoes it); the pending content saves go first.
    lockApproval (step) {
      var self = this

      if (!this.canLockApproval(step) || this.isStepFrozen(step.stepNo) || this.cmdRunning > 0 || this.lockRunning || !this.approvalDecided(step)) {
        return Promise.resolve({ ok: false, status: 'blocked' })
      }

      this.lockRunning = 'lock'

      return this.lockCommand(step).then(function (out) {
        self.lockRunning = null
        self.focusLockLine(out.ok ? 'unlock' : 'lock')

        return out
      })
    },

    // Does a remarks value say anything? An emptied quill editor stores `<p><br></p>`
    // (truthy), so "is there text" asks the text, not the string: any visible text,
    // or an image, counts. The body lives in `@/lib/remarksText`, shared with the
    // ReadOnlyRemarks atom.
    hasRemarksText (html) {
      return hasRemarksTextHelper(html)
    },

    // XOL-596 (Carlos, 2026-10-15: "We just need to avoid showing content that was not part of
    // the request form"): is this step row's `remarks` field part of the form for this request
    // type? A party-2 row's remarks are written only on an RTB, by the counterparties (the API's
    // RTB_PARTY_2_STEP_COUNTERPARTY_PATHS; no SPA surface writes it on another type), so on an
    // RTS, RTU or RTD there is nothing to fill in and its section is not shown. A row that
    // somehow carries text still shows, so nothing stored is hidden. A party-1 row (the Tower
    // Co's own) is always part of the form.
    stepRemarksInForm (step) {
      var a = this.actionData || this.rowData

      if (!step || Number(step.partyNo) !== 2) {
        return true
      }

      return (!!a && String(a.actionType || '').toUpperCase() === 'RTB') || this.hasRemarksText(step.remarks)
    },

    // XOL-596: the transform itself is `normaliseEquipment` in
    // lib/requestRowSync (pure, shared with the "did this field change"
    // comparison). This applies its result in place, exactly the keys the old
    // inline code touched.
    fixExistingEquipment () {
      if (this.actionData.state.step2.equipment) {
        var list = this.actionData.state.step2.equipment
        var fixed = sync.normaliseEquipment(list)
        var keys = ['category', 'type', 'subType', 'variant', 'name']

        for (var n = 0; n < list.length; n++) {
          var item = list[n]
          var out = fixed[n]

          if (!item || !out) {
            continue
          }

          for (var k = 0; k < keys.length; k++) {
            if (out[keys[k]] !== item[keys[k]]) {
              item[keys[k]] = out[keys[k]]
            }
          }
        }
      }
    }

  },

  watch: {
    stepData: function (step) {
      this.step = step
//      console.log('stepData changed', this.step)
    },

    step: function (step) {
//      console.log('step changed', this.step)
      this.$emit('updated', step)
    }

  }


}
